import React from "react";

const Programacion = React.memo(function Programacion({
  value,
  onChange,
  dayKeys,
  surfaceBgClass,
  surfaceHoverClass,
  isReadOnly,
}) {
  const inputRef = React.useRef(null);
  const safeValue = value && typeof value === "object" ? value : {};
  const days = safeValue.days && typeof safeValue.days === "object" ? safeValue.days : {};
  const times = Array.isArray(safeValue.times) ? safeValue.times : [];
  const freqMode = safeValue.freqMode || "fixed"; // "fixed" | "every" | "preset"
  const everyHours = safeValue.everyHours;
  const freqPreset = safeValue.freqPreset || null;
  const repeatCount =
    safeValue.repeatCount !== undefined && safeValue.repeatCount !== null ? safeValue.repeatCount : "";
  const [isFreqOpen, setIsFreqOpen] = React.useState(false);
  const frequencyOptions = React.useMemo(
    () => [
      { value: "fixed", label: "Horarios fijos" },
      { value: "before_breakfast", label: "Antes del desayuno" },
      { value: "before_lunch", label: "Antes del almuerzo" },
      { value: "before_dinner", label: "Antes de la cena" },
      { value: "before_each_meal", label: "Antes de cada comida" },
      { value: "after_breakfast", label: "Después del desayuno" },
      { value: "after_lunch", label: "Después del almuerzo" },
      { value: "after_dinner", label: "Después de la cena" },
      { value: "before_bed", label: "Antes de acostarse" },
      ...[1, 2, 4, 6, 8, 12, 24].map((n) => ({
        value: String(n),
        label: `Cada ${n} hora${n === 1 ? "" : "s"}`,
      })),
    ],
    []
  );
  const allSelected = React.useMemo(() => dayKeys.every((d) => days[d] === true), [dayKeys, days]);

  function setDays(nextDays) {
    onChange({ ...safeValue, days: nextDays });
  }
  function toggleDay(d) {
    setDays({ ...days, [d]: !days[d] });
  }
  function toggleAll() {
    const next = {};
    const nextVal = !allSelected;
    for (const d of dayKeys) next[d] = nextVal;
    setDays(next);
  }
  function isDigits(str) {
    for (let i = 0; i < str.length; i += 1) {
      const c = str.charCodeAt(i);
      if (c < 48 || c > 57) return false;
    }
    return str.length > 0;
  }
  function normalizeOne(raw) {
    if (!raw) return null;
    const s0 = String(raw).trim();
    if (!s0) return null;
    let hh = "";
    let mm = "";
    // HH:MM o H:MM
    if (s0.indexOf(":") >= 0) {
      const parts = s0.split(":");
      if (parts.length !== 2) return null;
      hh = parts[0];
      mm = parts[1];
    }
    // solo hora: 8 -> 08:00, 12 -> 12:00
    else if ((s0.length === 1 || s0.length === 2) && isDigits(s0)) {
      hh = s0;
      mm = "0";
    }
    // 830 o 1530
    else if ((s0.length === 3 || s0.length === 4) && isDigits(s0)) {
      const padded = ("0000" + s0).slice(-4);
      hh = padded.slice(0, 2);
      mm = padded.slice(2);
    } else {
      return null;
    }
    const h = Number(hh);
    const m = Number(mm);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (h < 0 || h > 23) return null;
    if (m < 0 || m > 59) return null;
    return ("0" + h).slice(-2) + ":" + ("0" + m).slice(-2);
  }
  function normalizeTimeList(raw) {
    if (!raw) return [];
    // Permite: "12,20" "12 20" "12;20" además de valores únicos.
    const s = String(raw).trim();
    if (!s) return [];
    // Normalizar separadores a espacios
    let normalized = s;
    normalized = normalized.split("\t").join(" ");
    normalized = normalized.split("\n").join(" ");
    normalized = normalized.split(";").join(" ");
    normalized = normalized.split(",").join(" ");
    return normalized
      .split(" ")
      .map((p) => p.trim())
      .filter((p) => p.length > 0)
      .map((p) => normalizeOne(p))
      .filter(Boolean);
  }
  function setFreqSelection(sel) {
    if (sel === "fixed") {
      onChange({
        ...safeValue,
        freqMode: "fixed",
        freqPreset: null,
        // mantener times tal cual
      });
      requestAnimationFrame(() => {
        if (inputRef.current) inputRef.current.focus();
      });
      return;
    }
    const n = Number(sel);
    if (Number.isFinite(n)) {
      const start = times[0] || null;
      onChange({
        ...safeValue,
        freqMode: "every",
        everyHours: n,
        freqPreset: null,
        times: start ? [start] : [],
      });
      return;
    }
    onChange({
      ...safeValue,
      freqMode: "preset",
      freqPreset: sel,
      // mantener times tal cual
    });
  }
  function addFixedTimes(raw) {
    const list = normalizeTimeList(raw);
    if (!list.length) return;
    const merged = Array.from(new Set([...times, ...list])).sort();
    onChange({ ...safeValue, freqMode: "fixed", times: merged });
  }
  function setStartTime(raw) {
    const t = normalizeOne(raw);
    if (!t) return;
    onChange({ ...safeValue, freqMode: "every", times: [t] });
  }
  function removeTime(t) {
    const next = times.filter((x) => x !== t);
    onChange({ ...safeValue, times: next });
    if (freqMode === "every" && next.length === 0) {
      requestAnimationFrame(() => {
        if (inputRef.current) inputRef.current.focus();
      });
    }
  }
  function onTimeKeyDown(e) {
    if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      if (freqMode === "fixed") {
        addFixedTimes(e.currentTarget.value);
      } else {
        setStartTime(e.currentTarget.value);
      }
      e.currentTarget.value = "";
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          if (inputRef.current.select) {
            inputRef.current.select();
          }
        }
      });
    }
  }
  const freqValue =
    freqMode === "fixed"
      ? "fixed"
      : freqMode === "preset"
        ? freqPreset || "before_breakfast"
        : String(typeof everyHours === "number" && Number.isFinite(everyHours) ? everyHours : 1);
  React.useEffect(() => {
    function handleOutsideClick(event) {
      if (!isFreqOpen) return;
      const target = event.target;
      if (target && target.closest && target.closest("[data-freq-select]")) return;
      setIsFreqOpen(false);
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isFreqOpen]);
  const frequencyMatch = frequencyOptions.find((opt) => opt.value === freqValue);
  const currentFrequencyLabel = frequencyMatch && frequencyMatch.label ? frequencyMatch.label : "Seleccione...";

  return (
    <div className="mt-3 rounded-xl border border-slate-200 p-2">
      <h3 className="text-sm font-semibold text-slate-700 mb-2">Cronograma</h3>
      {/* Días */}
      <div className="mb-2">
        <p className="text-xs text-slate-500 mb-1">Días</p>
        <div className="grid grid-cols-8 gap-2 text-center">
          {dayKeys.map((d) => {
            const isChecked = !!days[d];
            return (
              <label key={d} className="flex flex-col items-center">
                <span className="text-xs text-slate-600">{d}</span>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isChecked}
                  onClick={() => toggleDay(d)}
                  className={`relative mt-1 inline-flex h-4 w-4 items-center justify-center rounded border ${surfaceBgClass} ${
                    isChecked ? "border-[var(--primary)]" : "border-slate-300"
                  } focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
                >
                  <svg
                    viewBox="0 0 20 20"
                    className={`pointer-events-none h-3 w-3 text-[var(--primary)] transition-opacity ${
                      isChecked ? "opacity-100" : "opacity-0"
                    }`}
                    aria-hidden="true"
                  >
                    <path
                      d="M5 10.5l3 3 7-7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </label>
            );
          })}
          <label className="flex flex-col items-center">
            <span className="text-xs text-slate-600 font-medium">Todos</span>
            <button
              type="button"
              role="checkbox"
              aria-checked={allSelected}
              onClick={toggleAll}
              className={`relative mt-1 inline-flex h-4 w-4 items-center justify-center rounded border ${surfaceBgClass} ${
                allSelected ? "border-[var(--primary)]" : "border-slate-300"
              } focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
            >
              <svg
                viewBox="0 0 20 20"
                className={`pointer-events-none h-3 w-3 text-[var(--primary)] transition-opacity ${
                  allSelected ? "opacity-100" : "opacity-0"
                }`}
                aria-hidden="true"
              >
                <path
                  d="M5 10.5l3 3 7-7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </label>
        </div>
      </div>
      {/* Frecuencia (arriba de horarios) */}
      <div className="mb-2">
        <label className="block text-xs text-slate-500 mb-1">Frecuencia</label>
        <div className="relative" data-freq-select>
          <button
            type="button"
            onClick={() => setIsFreqOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={isFreqOpen}
            className={`flex w-full items-center justify-between rounded-lg ${surfaceBgClass} px-3 py-2 text-sm text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass} focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
          >
            <span>{currentFrequencyLabel}</span>
            <span className={`text-slate-400 transition ${isFreqOpen ? "rotate-180" : ""}`}>▾</span>
          </button>
          {isFreqOpen ? (
            <div className="absolute bottom-full z-20 mb-2 w-full rounded-xl bg-white shadow-lg ring-1 ring-slate-200">
              <div className="max-h-60 overflow-auto py-1 text-sm">
                {frequencyOptions.map((opt) => {
                  const isSelected = opt.value === freqValue;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        setFreqSelection(opt.value);
                        setIsFreqOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left ${
                        isSelected ? "bg-[var(--primary-50)] text-slate-700 font-medium" : "text-slate-700"
                      } hover:bg-slate-50`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {/* Horarios / Horario inicial */}
      {freqMode === "fixed" || freqMode === "every" ? (
        <div className="mb-2">
          <label className="block text-xs text-slate-500 mb-1">{freqMode === "fixed" ? "Horarios" : "Horario inicial"}</label>
          {times.length > 0 ? (
            <div className="mb-2 flex flex-wrap gap-2">
              {times.map((t) => (
                <span key={t} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-sm ring-1 ring-slate-200">
                  <span>{t}</span>
                  <button
                    type="button"
                    onClick={() => removeTime(t)}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                    aria-label="Eliminar horario"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          {freqMode === "fixed" || (freqMode === "every" && times.length === 0) ? (
            <>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                placeholder="HH:MM"
                onKeyDown={onTimeKeyDown}
                onFocus={(e) => e.currentTarget.select()}
                readOnly={isReadOnly}
                className={`w-full rounded-lg ${surfaceBgClass} px-3 py-2 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
              />
              {freqMode === "fixed" ? (
                <p className="mt-1 text-[11px] text-slate-500">TAB o Intro: añade. Formatos: 08:30, 830 o 12,20.</p>
              ) : (
                <p className="mt-1 text-[11px] text-slate-500">TAB o Intro: guarda el horario inicial.</p>
              )}
            </>
          ) : null}
        </div>
      ) : null}
      <div className="mt-2 w-full">
        <label className="block text-xs text-slate-500 mb-1">Validez en días (0 = hasta terminar).</label>
        <input
          type="number"
          inputMode="numeric"
          min="1"
          step="1"
          value={repeatCount}
          onChange={(e) => onChange({ ...safeValue, repeatCount: e.target.value })}
          readOnly={isReadOnly}
          className={`w-full rounded-lg ${surfaceBgClass} px-3 py-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
        />
      </div>
    </div>
  );
});

const NotificacionColorSelect = React.memo(function NotificacionColorSelect({
  value: selectedValue,
  onChange: onColorChange,
  options,
  surfaceBgClass,
  useMobileNotifications,
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const colorOptions = Array.isArray(options) ? options : [];
  const selected = colorOptions.find((opt) => opt.value === selectedValue) || colorOptions[0];
  const buttonClass = `flex h-9 w-full items-center gap-2 rounded-lg ${surfaceBgClass} px-2 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] justify-start${
    useMobileNotifications ? "" : " sm:justify-center"
  }`;
  const labelClass = useMobileNotifications ? "text-sm text-slate-700" : "text-sm text-slate-700 sm:hidden";
  const menuClass = `absolute right-0 bottom-full z-10 mb-2 w-full rounded-lg bg-white shadow-lg ring-1 ring-slate-200${
    useMobileNotifications ? "" : " sm:w-36"
  }`;

  React.useEffect(() => {
    if (!open) return;
    function onDocumentClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button type="button" onClick={() => setOpen((prev) => !prev)} className={buttonClass} aria-label="Color">
        <span className="h-4 w-4 rounded-sm" style={{ backgroundColor: selected ? selected.hex : undefined }} />
        <span className={labelClass}>{selected ? selected.label : ""}</span>
      </button>
      {open ? (
        <div className={menuClass}>
          {colorOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onColorChange(opt.value);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: opt.hex }} />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
});

const NotificacionFieldSelect = React.memo(function NotificacionFieldSelect({
  options,
  value: selectedValue,
  onChange: onFieldChange,
  surfaceBgClass,
  surfaceHoverClass,
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const selected = options && options.length ? options.find((opt) => opt.value === selectedValue) || options[0] : null;

  React.useEffect(() => {
    if (!open) return;
    function onDocumentClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  if (!options || options.length === 0) {
    return (
      <div className={`rounded-lg ${surfaceBgClass} px-2 py-2 text-sm text-slate-400 ring-1 ring-slate-200`}>
        Sin campos
      </div>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center justify-between rounded-lg ${surfaceBgClass} px-2 py-2 text-sm text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass} focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
      >
        <span className={selected ? "" : "text-slate-400"}>{selected ? selected.label : "Seleccione..."}</span>
        <span className={`text-slate-400 transition ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open ? (
        <div className="absolute bottom-full z-20 mb-2 w-full rounded-xl bg-white shadow-lg ring-1 ring-slate-200">
          <div className="max-h-56 overflow-auto py-1 text-xs">
            {options.map((opt) => {
              const isSelected = selected && selected.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    if (onFieldChange) onFieldChange(opt.value);
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left ${
                    isSelected ? "bg-[var(--primary-50)] text-slate-700 font-medium" : "text-slate-700"
                  } hover:bg-slate-50`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
});

const NOTIFICATION_OP_OPTIONS = [
  { value: "==", label: "=" },
  { value: ">", label: ">" },
  { value: "<", label: "<" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
  { value: "!=", label: "≠" },
  { value: "in", label: "in" },
  { value: "Tend", label: "Tendencia" },
];

const NotificacionOpSelect = React.memo(function NotificacionOpSelect({
  value: selectedValue,
  onChange: onOpChange,
  surfaceBgClass,
  surfaceHoverClass,
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const selected = NOTIFICATION_OP_OPTIONS.find((opt) => opt.value === selectedValue) || NOTIFICATION_OP_OPTIONS[0];

  React.useEffect(() => {
    if (!open) return;
    function onDocumentClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-9 w-full items-center justify-between rounded-lg ${surfaceBgClass} px-2 text-sm text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass} focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
      >
        <span>{selected ? selected.label : "Seleccione..."}</span>
        <span className={`text-slate-400 transition ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open ? (
        <div className="absolute bottom-full z-20 mb-2 w-full rounded-xl bg-white shadow-lg ring-1 ring-slate-200">
          <div className="max-h-56 overflow-auto py-1 text-sm">
            {NOTIFICATION_OP_OPTIONS.map((opt) => {
              const isSelected = selected && selected.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    if (onOpChange) onOpChange(opt.value);
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left ${
                    isSelected ? "bg-[var(--primary-50)] text-slate-700 font-medium" : "text-slate-700"
                  } hover:bg-slate-50`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
});
export default function FormCard(props) {
  const safeParams = props && props.params ? { ...props, ...props.params } : props || {};
  const {
    title = "Cuestionario",
    subtitle = "",
    fields: fieldsProp,
    icon_data_url = "",
    html_header,
    scheduled_time = "",
    result_value,
    editable: editableRaw,
    scheduling: schedulingRaw,
    notification: notificationRaw,
    header: headerRaw,
    close: closeRaw,
    card: cardRaw,
    submit: submitRaw,
    height,
    color: colorProp,
    background_color: backgroundColorProp,
    backgroundColor: backgroundColorAlias,
    dark: darkRaw,
    onOperationChange,
    scheduling_value,
    notification_value,
    onProgramacionChange,
    onNotificacionRulesChange,
    builderMode,
    builderSelectedId,
    onBuilderSelectField,
    onBuilderRemoveField,
    onBuilderMoveField,
    onBuilderDropAt,
    onBuilderSelectForm,
    onBuilderFieldValueChange,
    onBuilderAdderClone,
    onBuilderRemoveAdderGroup,
    forceMobileNotifications,
  } = safeParams;
  const isBuilderMode = builderMode === true || String(builderMode).toLowerCase() === "true";
  const scheduling = schedulingRaw !== undefined ? schedulingRaw : "false";
  const notification = notificationRaw !== undefined ? notificationRaw : "false";
  const editable = editableRaw !== undefined ? editableRaw : "false";
  const header = headerRaw !== undefined ? headerRaw : "true";
  const close = closeRaw !== undefined ? closeRaw : "false";
  const card = cardRaw !== undefined ? cardRaw : "true";
  const submit = submitRaw !== undefined ? submitRaw : "false";
  const dark = darkRaw !== undefined ? darkRaw : "false";
  const isDark = String(dark).toLowerCase() === "true";
  const primaryColor = typeof colorProp === "string" && colorProp.trim() ? colorProp.trim() : "#0ea5e9";
  const primaryVars = React.useMemo(() => buildPrimaryVars(primaryColor), [primaryColor]);
  const backgroundColorValue =
    isDark
      ? ""
      : typeof backgroundColorProp === "string" && backgroundColorProp.trim()
        ? backgroundColorProp.trim()
        : typeof backgroundColorAlias === "string" && backgroundColorAlias.trim()
          ? backgroundColorAlias.trim()
          : "";
  const defaultSurfaceVars = React.useMemo(
    () => ({
      "--surface-50": "#f8fafc",
      "--surface-100": "#f1f5f9",
      "--surface-200": "#e2e8f0",
    }),
    []
  );
  const darkSurfaceVars = React.useMemo(
    () => ({
      "--surface-50": "#0f172a",
      "--surface-100": "#111827",
      "--surface-200": "#1f2937",
    }),
    []
  );
  const surfaceVars = React.useMemo(() => {
    if (isDark) return darkSurfaceVars;
    const built = buildSurfaceVars(backgroundColorValue);
    return Object.keys(built).length ? built : defaultSurfaceVars;
  }, [backgroundColorValue, defaultSurfaceVars, darkSurfaceVars, isDark]);
  const sectionStyle = isDark
    ? { ...primaryVars, ...surfaceVars, backgroundColor: surfaceVars["--surface-50"] }
    : backgroundColorValue
      ? { ...primaryVars, ...surfaceVars, backgroundColor: backgroundColorValue }
      : primaryVars;
  const surfaceBgClass = isDark ? "bg-slate-900" : backgroundColorValue ? "bg-[var(--surface-50)]" : "bg-slate-50";
  const surfaceBgStrongClass = isDark ? "bg-slate-800" : backgroundColorValue ? "bg-[var(--surface-100)]" : "bg-slate-100";
  const surfaceBgStrongerClass = isDark ? "bg-slate-700" : backgroundColorValue ? "bg-[var(--surface-200)]" : "bg-slate-200";
  const surfaceHoverClass = isDark ? "hover:bg-slate-800" : backgroundColorValue ? "hover:bg-[var(--surface-100)]" : "hover:bg-slate-100";
  const surfaceHoverStrongClass = isDark ? "hover:bg-slate-700" : backgroundColorValue ? "hover:bg-[var(--surface-200)]" : "hover:bg-slate-200";
  const softButtonClass = isDark
    ? `${surfaceBgStrongClass} text-slate-100 ring-1 ring-slate-700 ${surfaceHoverStrongClass}`
    : `${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass}`;
  const tabBarBackground = React.useMemo(() => {
    if (!backgroundColorValue) return "";
    const darker = darkenColor(backgroundColorValue, 0.06);
    return darker || backgroundColorValue;
  }, [backgroundColorValue]);
  const darkModeStyles = React.useMemo(
    () =>
      [
        ".formcard-dark{color:#e2e8f0;}",
        ".formcard-dark .text-slate-900{color:#f8fafc !important;}",
        ".formcard-dark .text-slate-800{color:#f1f5f9 !important;}",
        ".formcard-dark .text-slate-700{color:#e2e8f0 !important;}",
        ".formcard-dark .text-slate-600{color:#cbd5f5 !important;}",
        ".formcard-dark .text-slate-500{color:#94a3b8 !important;}",
        ".formcard-dark .text-slate-400{color:#94a3b8 !important;}",
        ".formcard-dark .text-slate-300{color:#cbd5f5 !important;}",
        ".formcard-dark .bg-white{background-color:#0f172a !important;}",
        ".formcard-dark .bg-slate-50{background-color:#0f172a !important;}",
        ".formcard-dark .bg-slate-100{background-color:#111827 !important;}",
        ".formcard-dark .bg-slate-200{background-color:#1f2937 !important;}",
        ".formcard-dark .border-slate-200{border-color:#334155 !important;}",
        ".formcard-dark .border-slate-300{border-color:#475569 !important;}",
        ".formcard-dark .ring-slate-200{--tw-ring-color:#334155 !important;}",
        ".formcard-dark .ring-slate-300{--tw-ring-color:#475569 !important;}",
        ".formcard-dark .ring-slate-400{--tw-ring-color:#64748b !important;}",
        ".formcard-dark .hover\\:bg-slate-50:hover{background-color:#111827 !important;}",
        ".formcard-dark .hover\\:bg-slate-100:hover{background-color:#1f2937 !important;}",
        ".formcard-dark .hover\\:bg-slate-200:hover{background-color:#334155 !important;}",
        ".formcard-dark input::placeholder,.formcard-dark textarea::placeholder{color:#94a3b8 !important;}",
      ].join(""),
    []
  );
  const normalizeFieldGroupAlias = React.useCallback((field) => {
    if (!field || typeof field !== "object") return field;
    const next = { ...field };
    if (next.adderGroup !== undefined && next.adderGroup !== null && String(next.adderGroup).trim() !== "") {
      next.adderGroup = String(next.adderGroup);
    }
    if (Object.prototype.hasOwnProperty.call(next, "group")) {
      delete next.group;
    }
    return next;
  }, []);
  const fieldsValueKey =
    typeof fieldsProp === "string" ? fieldsProp : JSON.stringify(Array.isArray(fieldsProp) ? fieldsProp : []);
  const baseFields = React.useMemo(() => {
    const rawFields = Array.isArray(fieldsProp) ? fieldsProp : JSON.parse(fieldsValueKey || "[]");
    if (!Array.isArray(rawFields)) return [];
    return rawFields.map((field) => normalizeFieldGroupAlias(field));
  }, [fieldsProp, fieldsValueKey]);
  const [dynamicFields, setDynamicFields] = React.useState(baseFields);
  React.useEffect(() => {
    setDynamicFields(baseFields);
  }, [baseFields]);
  const fields = dynamicFields;
  const [builderDragOverId, setBuilderDragOverId] = React.useState(null);
  const canHandleBuilderDrop = Boolean(onBuilderDropAt || onBuilderMoveField);
  const canSelectForm = isBuilderMode && typeof onBuilderSelectForm === "function";
  const builderFieldIdByName = React.useMemo(() => {
    const map = {};
    for (let i = 0; i < fields.length; i += 1) {
      const field = fields[i];
      if (!field || !field.name) continue;
      map[field.name] = field._id || "";
    }
    return map;
  }, [fields]);
  const adderGroupIdRef = React.useRef(1);
  const PAIN_SCALE = React.useMemo(
    () => [
      { value: "aliviado", label: "Muy aliviado", emoji: "😌" },
      { value: "leve", label: "Leve", emoji: "🙂" },
      { value: "medio", label: "Moderado", emoji: "😐" },
      { value: "dolor", label: "Dolor", emoji: "😣" },
      { value: "intenso", label: "Muy doloroso", emoji: "😫" },
    ],
    []
  );
  const PAIN_KEYS = React.useMemo(() => PAIN_SCALE.map((item) => item.value), [PAIN_SCALE]);
  
  const MAX_DICTATION_CHARS = 50000;
  
  const ATTACH_VIEWER_URL = React.useMemo(
    () => "https://docs.google.com/gview?embedded=1&url={url}",
    []
  );
  
  const IMAGE_EXTENSIONS = React.useMemo(
    () => new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"]),
    []
  );
  
  const RATING_COMMENT_SUFFIX = "_comentario";
  
  const DAY_KEYS = React.useMemo(
    () => ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
    []
  );

  const VALUE_FIELD_TYPES = React.useMemo(
    () =>
      new Set([
        "text",
        "number",
        "date",
        "time",
        "datetime",
        "memo",
        "options",
        "singlelist",
        "multiplelist",
        "bool",
        "slider",
        "rating",
        "pain",
        "selector",
        "image",
        "video",
        "attach",
      ]),
    []
  );
  
  
  const NOTIFICATION_COLOR_OPTIONS = React.useMemo(
    () => [
      { value: "red", label: "Urgente", hex: "#ef4444" },
      { value: "amber", label: "Moderado", hex: "#f59e0b" },
      { value: "green", label: "Leve", hex: "#10b981" },
      { value: "blue", label: "Información", hex: "#3b82f6" },
    ],
    []
  );
  
  const NOTIFICATION_TREND_OPTIONS =  React.useMemo(
    () => [
      { value: "down", label: "A la baja" },
      { value: "up", label: "Al alza" },
      { value: "normal", label: "Normal" },
    ],
    []
  );  
  const DICTATION_REPLACEMENTS = React.useMemo(
    () =>
      [
        { phrase: "signo de apertura de interrogación", value: "¿" },
        { phrase: "signo de apertura de interrogacion", value: "¿" },
        { phrase: "signo de apertura de exclamación", value: "¡" },
        { phrase: "signo de apertura de exclamacion", value: "¡" },
        { phrase: "signo de interrogación", value: "?" },
        { phrase: "signo de interrogacion", value: "?" },
        { phrase: "signo de exclamación", value: "!" },
        { phrase: "signo de exclamacion", value: "!" },
        { phrase: "punto y coma", value: ";" },
        { phrase: "punto y aparte", value: ".\n\n" },
        { phrase: "punto y seguido", value: "." },
        { phrase: "punto aparte", value: ".\n\n" },
        { phrase: "punto seguido", value: "." },
        { phrase: "dos puntos", value: ":" },
        { phrase: "abre interrogación", value: "¿" },
        { phrase: "abre interrogacion", value: "¿" },
        { phrase: "abrir interrogación", value: "¿" },
        { phrase: "abrir interrogacion", value: "¿" },
        { phrase: "abre exclamación", value: "¡" },
        { phrase: "abre exclamacion", value: "¡" },
        { phrase: "abrir exclamación", value: "¡" },
        { phrase: "abrir exclamacion", value: "¡" },
        { phrase: "abre paréntesis", value: "(" },
        { phrase: "abre parentesis", value: "(" },
        { phrase: "abrir paréntesis", value: "(" },
        { phrase: "abrir parentesis", value: "(" },
        { phrase: "cierra paréntesis", value: ")" },
        { phrase: "cierra parentesis", value: ")" },
        { phrase: "cerrar paréntesis", value: ")" },
        { phrase: "cerrar parentesis", value: ")" },
        { phrase: "comillas dobles", value: "\"" },
        { phrase: "comilla doble", value: "\"" },
        { phrase: "comillas simples", value: "'" },
        { phrase: "comilla simple", value: "'" },
        { phrase: "guion bajo", value: "_" },
        { phrase: "guión bajo", value: "_" },
        { phrase: "guion", value: "-" },
        { phrase: "guión", value: "-" },
        { phrase: "nueva línea", value: "\n" },
        { phrase: "nueva linea", value: "\n" },
        { phrase: "salto de línea", value: "\n" },
        { phrase: "salto de linea", value: "\n" },
        { phrase: "punto", value: "." },
        { phrase: "coma", value: "," },
        { phrase: "enter", value: "\n" },
      ].sort((a, b) => b.phrase.length - a.phrase.length),
    []
  );
  function IconForm({ className }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className={className}
      >
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <path d="M8 7h8" />
        <path d="M8 11h8" />
        <path d="M8 15h5" />
        <path d="M7.5 18l1.5 1.5L12 16.5" />
      </svg>
    );
  }
  function IconBell({ className }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className={className}
      >
        <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
        <path d="M10 19a2 2 0 0 0 4 0" />
      </svg>
    );
  }
  function resolveHeaderIcon(nextIconDataUrl, DefaultIcon) {
    if (nextIconDataUrl) return <img src={nextIconDataUrl} alt="" className="h-6 w-6 object-contain" />;
    return <DefaultIcon className="h-6 w-6 text-slate-700" />;
  }
  function capitalizeFirst(text) {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
  }
  function coalesce(value, fallback) {
    return value === undefined || value === null ? fallback : value;
  }
  function isTrueValue(value) {
    if (value === true) return true;
    if (typeof value === "string") return value.toLowerCase() === "true";
    return false;
  }
  function parseMaxLengthValue(value) {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, Math.floor(value));
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.max(0, Math.floor(parsed));
    return null;
  }
  function resolveNumberMaxLength(field) {
    if (!field) return undefined;
    const parsed = parseMaxLengthValue(field.maxLength);
    return parsed === null ? undefined : parsed;
  }
  function resolveNumberStep(field) {
    if (!field) return undefined;
    const raw = field.step;
    if (raw !== undefined && raw !== null && raw !== "") return raw;
    if (isTrueValue(field.integer)) return 1;
    return undefined;
  }
  function sanitizeNumberInput(field, rawValue) {
    const text = rawValue === undefined || rawValue === null ? "" : String(rawValue);
    const maxLength = parseMaxLengthValue(field ? field.maxLength : null);
    const integerMode = field && isTrueValue(field.integer);
    if (integerMode) {
      let digits = "";
      for (let i = 0; i < text.length; i += 1) {
        const ch = text.charAt(i);
        if (ch >= "0" && ch <= "9") {
          digits += ch;
        }
      }
      if (maxLength !== null) {
        digits = digits.slice(0, maxLength);
      }
      return digits;
    }
    if (maxLength !== null) {
      let count = 0;
      let output = "";
      for (let i = 0; i < text.length; i += 1) {
        const ch = text.charAt(i);
        const isDigit = ch >= "0" && ch <= "9";
        if (isDigit) {
          if (count >= maxLength) continue;
          count += 1;
        }
        output += ch;
      }
      return output;
    }
    return text;
  }
  function isFieldReadOnly(field) {
    if (!field) return false;
    const raw = field.readonly !== undefined ? field.readonly : field.readOnly;
    if (raw === undefined || raw === null) return false;
    if (typeof raw === "boolean") return raw;
    return String(raw).toLowerCase() === "true";
  }
  function normalizeHexColor(value) {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    const hex = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
    if (!/^[0-9a-fA-F]+$/.test(hex)) return "";
    if (hex.length === 3) {
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }
    if (hex.length === 6) {
      return `#${hex}`;
    }
    return "";
  }
  function hexToRgb(value) {
    const normalized = normalizeHexColor(value);
    if (!normalized) return null;
    const hex = normalized.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some((c) => Number.isNaN(c))) return null;
    return { r, g, b };
  }
  function buildPrimaryVars(value) {
    const trimmed = typeof value === "string" ? value.trim() : "";
    const normalized = normalizeHexColor(trimmed);
    const rgb = hexToRgb(normalized || "#0ea5e9") || { r: 14, g: 165, b: 233 };
    const baseColor = normalized || (trimmed ? trimmed : "#0ea5e9");
    return {
      "--primary": baseColor,
      "--primary-50": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)`,
      "--primary-100": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.16)`,
      "--primary-200": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.28)`,
    };
  }
  function darkenColor(value, amount = 0.06) {
    const rgb = hexToRgb(value);
    if (!rgb) return "";
    const factor = 1 - Math.min(Math.max(amount, 0), 1);
    const r = Math.max(0, Math.round(rgb.r * factor));
    const g = Math.max(0, Math.round(rgb.g * factor));
    const b = Math.max(0, Math.round(rgb.b * factor));
    return `rgb(${r}, ${g}, ${b})`;
  }
  function mixRgb(source, target, amount) {
    const ratio = Math.min(Math.max(amount, 0), 1);
    const r = Math.round(source.r + (target.r - source.r) * ratio);
    const g = Math.round(source.g + (target.g - source.g) * ratio);
    const b = Math.round(source.b + (target.b - source.b) * ratio);
    return { r, g, b };
  }
  function buildSurfaceVars(value) {
    const rgb = hexToRgb(value);
    if (!rgb) return {};
    const white = { r: 255, g: 255, b: 255 };
    const soft = mixRgb(rgb, white, 0.92);
    const softStrong = mixRgb(rgb, white, 0.88);
    const softStronger = mixRgb(rgb, white, 0.84);
    return {
      "--surface-50": `rgb(${soft.r}, ${soft.g}, ${soft.b})`,
      "--surface-100": `rgb(${softStrong.r}, ${softStrong.g}, ${softStrong.b})`,
      "--surface-200": `rgb(${softStronger.r}, ${softStronger.g}, ${softStronger.b})`,
    };
  }
  function normalizeHeight(value) {
    if (value === undefined || value === null) return "";
    if (typeof value === "number" && Number.isFinite(value)) return `${value}px`;
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
    return trimmed;
  }
  function formatReminderLabel(nextScheduledTime, postponeMinutes = 0) {
    const base = nextScheduledTime ? new Date(nextScheduledTime) : new Date();
    const safeBase = Number.isNaN(base.getTime()) ? new Date() : base;
    const date = new Date(safeBase.getTime() + postponeMinutes * 60000);
    const dayLabel = capitalizeFirst(new Intl.DateTimeFormat("es-ES", { weekday: "long" }).format(date));
    const timeLabel = new Intl.DateTimeFormat("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
    return { dayLabel, timeLabel };
  }
  function LabelText({ text, required }) {
    return (
      <span className="block text-xs font-medium text-slate-600" data-label-text>
        {text}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </span>
    );
  }
  function StreamVideo({ stream, className, controls = false }) {
    const videoEl = React.useRef(null);
    React.useEffect(() => {
      const el = videoEl.current;
      if (!el) return;
      try {
        if (el.srcObject !== stream) {
          el.srcObject = stream;
        }
        if (el.play) {
          const playPromise = el.play();
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => {});
          }
        }
      } catch (err) {
        // no-op
      }
      return () => {
        if (!el) return;
        try {
          if (el.srcObject === stream) {
            el.srcObject = null;
          }
        } catch (err) {
          // no-op
        }
      };
    }, [stream]);
    return <video ref={videoEl} className={className} playsInline muted controls={controls} />;
  }
  function autoResizeTextarea(element) {
    if (!element) return;
    const pending = resizeRafRef.current.get(element);
    if (pending) cancelAnimationFrame(pending);
    const rafId = requestAnimationFrame(() => {
      resizeRafRef.current.delete(element);
      element.style.height = "auto";
      const nextHeight = `${element.scrollHeight}px`;
      if (element.style.height !== nextHeight) {
        element.style.height = nextHeight;
      }
    });
    resizeRafRef.current.set(element, rafId);
  }
  function getRatingCommentKey(name) {
    return `${name}${RATING_COMMENT_SUFFIX}`;
  }
  function getImageViewOptions(field) {
    if (!field) return [];
    if (Array.isArray(field.options)) return field.options.filter(Boolean);
    if (Array.isArray(field.images)) return field.images.filter(Boolean);
    return [];
  }
  function getSelectorOptions(field) {
    if (!field || !Array.isArray(field.options)) return [];
    return field.options.filter(Boolean).slice(0, 6);
  }
  function getSelectorImageSrc(optionValue) {
    if (typeof optionValue !== "string") return "";
    const trimmed = optionValue.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("data:")) return trimmed;
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("blob:") || trimmed.startsWith("/")) {
      return trimmed;
    }
    return `data:image/png;base64,${trimmed}`;
  }
  function getSelectedIndex(value, options) {
    if (!options || options.length === 0) return -1;
    if (value === undefined || value === null) return -1;
    if (typeof value === "number" && Number.isFinite(value)) {
      const idx = Math.floor(value);
      return idx >= 1 && idx <= options.length ? idx - 1 : -1;
    }
    const raw = String(value).trim();
    if (!raw) return -1;
    if (/^\d+$/.test(raw)) {
      const idx = Number(raw);
      return Number.isFinite(idx) && idx >= 1 && idx <= options.length ? idx - 1 : -1;
    }
    const idx = options.indexOf(raw);
    return idx >= 0 ? idx : -1;
  }
  function normalizeLayoutFreeOptions(rawOptions) {
    if (!rawOptions) return [];
    if (Array.isArray(rawOptions)) {
      return rawOptions
        .map((item) => String(item).trim())
        .filter((item) => item);
    }
    if (typeof rawOptions === "string") {
      return rawOptions
        .split(/\r?\n|,/)
        .map((item) => item.trim())
        .filter((item) => item);
    }
    return [];
  }
  function isLayoutField(field) {
    return (
      field &&
      (field.type === "layout-1" || field.type === "layout-2" || field.type === "layout-3" || field.type === "layout-free")
    );
  }
  function isTabField(field) {
    return field && field.type === "tab";
  }
  function isControlField(field) {
    return isLayoutField(field) || isTabField(field);
  }
  function getTextStyle(field) {
    if (!field) return undefined;
    const style = {};
    let fontSizeRaw;
    if (field["font-size"] !== undefined) {
      fontSizeRaw = field["font-size"];
    } else if (field.fontSize !== undefined) {
      fontSizeRaw = field.fontSize;
    }
    if (fontSizeRaw !== undefined && fontSizeRaw !== null && String(fontSizeRaw).trim() !== "") {
      style.fontSize = fontSizeRaw;
    }
    const alignmentRaw = typeof field.alignment === "string" ? field.alignment.toLowerCase() : field.alignment;
    if (alignmentRaw === "left" || alignmentRaw === "center" || alignmentRaw === "right") {
      style.textAlign = alignmentRaw;
    }
    if (field.color !== undefined && field.color !== null && String(field.color).trim() !== "") {
      style.color = field.color;
    }
    return Object.keys(style).length > 0 ? style : undefined;
  }
  function getVariantClass(variant, fallback) {
    if (variant === "strong") return "text-slate-800 font-semibold";
    if (variant === "subtle") return "text-slate-400";
    if (variant === "muted") return "text-slate-600";
    return fallback;
  }
  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  const dictationReplacementPatterns = React.useMemo(
    () =>
      DICTATION_REPLACEMENTS.map((item) => ({
        regex: new RegExp(`(^|\\s)${escapeRegExp(item.phrase)}(?=\\s|$)`, "gi"),
        value: item.value,
      })),
    [DICTATION_REPLACEMENTS]
  );
  function extractBase64FromDataUrl(dataUrl) {
    if (typeof dataUrl !== "string") return "";
    const marker = "base64,";
    const index = dataUrl.indexOf(marker);
    if (index === -1) return "";
    return dataUrl.slice(index + marker.length);
  }
  function setFilePayload(fieldName, file) {
    if (!fieldName) return;
    if (!file) {
      set_file_payloads((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
      return;
    }
    if (typeof file === "string") {
      set_file_payloads((prev) => ({ ...prev, [fieldName]: file }));
      return;
    }
    const basePayload = {
      name: typeof file.name === "string" ? file.name : "",
      size: typeof file.size === "number" ? file.size : undefined,
      type: typeof file.type === "string" ? file.type : "",
    };
    set_file_payloads((prev) => ({ ...prev, [fieldName]: basePayload }));
    if (typeof File !== "undefined" && file instanceof File) {
      const nextToken = (filePayloadTokenRef.current[fieldName] || 0) + 1;
      filePayloadTokenRef.current[fieldName] = nextToken;
      const reader = new FileReader();
      reader.onload = () => {
        if (filePayloadTokenRef.current[fieldName] !== nextToken) return;
        const dataUrl = typeof reader.result === "string" ? reader.result : "";
        const base64 = extractBase64FromDataUrl(dataUrl);
        if (!base64) return;
        const payloadWithBase64 = { ...basePayload, base64 };
        set_file_payloads((prev) => ({ ...prev, [fieldName]: payloadWithBase64 }));
        notifyBuilderValueChange(fieldName, payloadWithBase64);
      };
      reader.readAsDataURL(file);
    }
  }
  function applyDictationPunctuation(input) {
    if (!input) return "";
    let output = input;
    for (const { regex, value } of dictationReplacementPatterns) {
      regex.lastIndex = 0;
      output = output.replace(regex, (match, prefix) => `${prefix}${value}`);
    }
    output = output.replace(/\\s+([,.;:!?])/g, "$1");
    output = output.replace(/\\s+([)\\]])/g, "$1");
    output = output.replace(/([¿¡(\\[])\s+/g, "$1");
    output = output.replace(/([,.;:!?])([^\\s\\n)\\]])/g, "$1 $2");
    output = output.replace(/[ \\t]+\\n/g, "\\n");
    return output;
  }
  function appendDictationText(current, addition) {
    if (!addition) return coalesce(current, "");
    if (!current) return addition;
    const needsSpace = !/[\\s\\n]$/.test(current);
    const startsWithNoSpace = /^[,.;:!?)]/.test(addition);
    const startsWithNewline = /^\\n/.test(addition);
    const separator = needsSpace && !startsWithNoSpace && !startsWithNewline ? " " : "";
    return `${current}${separator}${addition}`;
  }
  function serializeSubmitValue(value) {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    try {
      return JSON.stringify(value);
    } catch (err) {
      return String(value);
    }
  }
  function findClosestForm(element) {
    let current = element;
    while (current) {
      if (current.tagName === "FORM") return current;
      current = current.parentElement;
    }
    return null;
  }
  const headerIcon = resolveHeaderIcon(icon_data_url, IconForm);
  const hasHtmlHeader = typeof html_header === "string" && html_header.trim() !== "";
  const hasScheduledTime = Boolean(scheduled_time && String(scheduled_time).trim() !== "");
  const hasSubtitle = typeof subtitle === "string" ? subtitle.trim() !== "" : Boolean(subtitle);
  const showHeader = String(header).toLowerCase() === "true";
  const showClose = String(close).toLowerCase() === "true";
  const showCard = String(card).toLowerCase() === "true";
  const darkClassName = isDark ? "formcard-dark" : "";
  const cardClassName = showCard
    ? `relative rounded-2xl px-3 py-4 shadow-sm ring-1 ${
        isDark ? "bg-slate-900 ring-slate-700 text-slate-100" : `ring-slate-200 ${backgroundColorValue ? "" : "bg-white"}`
      } ${darkClassName}`
    : `relative ${darkClassName}`;
  const effectiveHeight = isBuilderMode ? undefined : height;
  const scrollHeight = normalizeHeight(effectiveHeight);
  const scrollContainerStyle = scrollHeight
    ? {
        height: scrollHeight,
        overflowY: "auto",
        paddingTop: "2px",
        paddingLeft: "2px",
        paddingRight: "12px",
        paddingBottom: "12px",
      }
    : undefined;
  const fixedHeightStyle = scrollHeight ? { height: scrollHeight } : undefined;
  const innerScrollStyle = scrollHeight
    ? { overflowY: "auto", paddingTop: "2px", paddingLeft: "2px", paddingRight: "12px", paddingBottom: "12px" }
    : undefined;
  const submitMode = String(submit).toLowerCase() === "true";
  const isEditable = String(editable).toLowerCase() === "true";
  const hasResultValue = result_value !== undefined && result_value !== null && (typeof result_value === "string" ? result_value.trim() !== "" : true);
  const isReadOnly = false;
  const showNotificacion = String(notification).toLowerCase() === "true" && !isReadOnly;
  const parsedResultValue = React.useMemo(() => parseResultValue(result_value), [result_value]);
  const inputFieldOptions = React.useMemo(
    () => {
      if (!showNotificacion) return [];
      return fields
        .filter((field) => isInputField(field))
        .map((field) => ({
          value: field.name,
          label: getFieldLabel(field),
        }));
    },
    [fields, showNotificacion]
  );
  const tabConfig = React.useMemo(() => {
    const before = [];
    const tabs = [];
    const tabMap = {};
    const order = [];
    let inTabs = false;
    let currentTab = null;
    for (let i = 0; i < fields.length; i += 1) {
      const field = fields[i];
      if (!field) continue;
      if (isTabField(field)) {
        inTabs = true;
        let label = "";
        if (field.tab !== undefined && field.tab !== null) {
          label = String(field.tab);
        } else if (field.label !== undefined && field.label !== null) {
          label = String(field.label);
        }
        if (label && label.trim) {
          label = label.trim();
        }
        if (!label) {
          label = "Pestaña";
        }
        if (!tabMap[label]) {
          tabMap[label] = { label: label, fields: [] };
          order.push(label);
        }
        currentTab = label;
        continue;
      }
      if (!inTabs) {
        before.push(field);
        continue;
      }
      if (!currentTab) {
        const fallback = "Pestaña";
        if (!tabMap[fallback]) {
          tabMap[fallback] = { label: fallback, fields: [] };
          order.push(fallback);
        }
        currentTab = fallback;
      }
      tabMap[currentTab].fields.push(field);
    }
    for (let i = 0; i < order.length; i += 1) {
      const key = order[i];
      if (tabMap[key]) {
        tabs.push(tabMap[key]);
      }
    }
    return {
      before: before,
      tabs: tabs,
      order: order,
      hasTabs: order.length > 0,
    };
  }, [fields]);

  function parseJsonValue(raw) {
    if (raw === undefined || raw === null || raw === "") return null;
    if (typeof raw === "object") return raw;
    if (typeof raw !== "string") return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return null;
    }
  }

  function mergeProgramacion(baseValue, rawValue) {
    const base = baseValue || {};
    const raw = rawValue && typeof rawValue === "object" ? rawValue : null;
    const next = { ...base, ...(raw || {}) };
    next.days = { ...(base.days || {}), ...((raw && raw.days) || {}) };
    next.times = Array.isArray(raw && raw.times) ? [...raw.times] : Array.isArray(base.times) ? [...base.times] : [];
    return next;
  }

  function normalizeSchedulingValue(rawValue) {
    const parsed = parseJsonValue(rawValue);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  }

  function normalizeNotificationRules(rawValue) {
    const parsed = parseJsonValue(rawValue);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === "object" && Array.isArray(parsed.rules)) {
      return parsed.rules;
    }
    return [];
  }

  const defaultProgramacion = React.useMemo(
    () => ({
      days: { Lun: false, Mar: false, Mie: false, Jue: false, Vie: false, Sab: false, Dom: false },
      freqMode: "fixed",
      freqPreset: null,
      everyHours: 1,
      times: [],
      repeatWeekly: false,
      repeatCount: "1",
    }),
    []
  );
  const initialProgramacion = React.useMemo(() => {
    const parsed = normalizeSchedulingValue(scheduling_value);
    return mergeProgramacion(defaultProgramacion, parsed);
  }, [defaultProgramacion, scheduling_value]);
  const initialNotificacionRules = React.useMemo(() => normalizeNotificationRules(notification_value), [notification_value]);
  const initialValues = React.useMemo(() => {
    const next = {};
    for (const field of fields) {
      if (!field || !field.name) continue;
      if (isControlField(field) || field.type === "adder") continue;
      const defaultValue = getDefaultValue(field);
      if (field.type === "rating" && defaultValue && typeof defaultValue === "object") {
        const ratingValue = Number(coalesce(defaultValue.value, defaultValue.rating)) || 0;
        const commentValue =
          typeof defaultValue.comment === "string"
            ? defaultValue.comment
            : typeof defaultValue.comentario === "string"
              ? defaultValue.comentario
              : "";
        next[field.name] = ratingValue;
        next[getRatingCommentKey(field.name)] = commentValue;
      } else {
        next[field.name] = defaultValue;
        if (field.type === "rating") {
          next[getRatingCommentKey(field.name)] = "";
        }
      }
      if (parsedResultValue && Object.prototype.hasOwnProperty.call(parsedResultValue, field.name)) {
        const rawValue = parsedResultValue[field.name];
        if (field.type === "rating" && rawValue && typeof rawValue === "object") {
          const ratingValue = Number(coalesce(rawValue.value, rawValue.rating)) || 0;
          const commentValue =
            typeof rawValue.comment === "string"
              ? rawValue.comment
              : typeof rawValue.comentario === "string"
                ? rawValue.comentario
                : "";
          next[field.name] = ratingValue;
          next[getRatingCommentKey(field.name)] = commentValue;
        } else {
          if (field.type === "memo" && rawValue === 0) {
            next[field.name] = "";
          } else {
            next[field.name] = rawValue;
          }
        }
      } else if (field.type === "rating") {
        const commentKey = getRatingCommentKey(field.name);
        if (parsedResultValue && Object.prototype.hasOwnProperty.call(parsedResultValue, commentKey)) {
          const rawComment = parsedResultValue[commentKey];
          next[commentKey] = typeof rawComment === "string" ? rawComment : String(coalesce(rawComment, ""));
        }
      }
    }
    return next;
  }, [fields, parsedResultValue]);
  const [values, setValues] = React.useState(initialValues);
  
  const [, setOperationState] = React.useState("");
  const postponeMinutes = 0;
  function setOperation(nextValue) {
    setOperationState(nextValue);
    if (typeof onOperationChange === "function") onOperationChange(nextValue);
  }
  const [viewer, setViewer] = React.useState({ open: false, type: "", src: "", caption: "" });
  const [viewerImageError, setViewerImageError] = React.useState(false);
  const [openSelectField, setOpenSelectField] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("");
  const [dictationField, setDictationField] = React.useState(null);
  const [dictationError, setDictationError] = React.useState({ field: null, message: "" });
  const [dictationPhase, setDictationPhase] = React.useState("idle");
  const [dictationPreview, setDictationPreview] = React.useState({ field: null, text: "" });
  const [internalProgramacion, setInternalProgramacion] = React.useState(initialProgramacion);
  const [notificacionRules, setNotificacionRules] = React.useState(initialNotificacionRules);
  const programacionKeyRef = React.useRef(JSON.stringify(initialProgramacion));
  const notificacionKeyRef = React.useRef(JSON.stringify(initialNotificacionRules));
  const [activeCameraField, setActiveCameraField] = React.useState(null);
  const [cameraFacing, setCameraFacing] = React.useState("environment");
  const [cameraError, setCameraError] = React.useState({ field: null, message: "" });
  const [imagePreviews, setImagePreviews] = React.useState({});
  const [videoPreviews, setVideoPreviews] = React.useState({});
  const [attachPreviews, setAttachPreviews] = React.useState({});
  const [file_payloads, set_file_payloads] = React.useState({});
  const [recordingField, setRecordingField] = React.useState(null);
  const [recordingSecondsLeft, setRecordingSecondsLeft] = React.useState(0);
  const videoRef = React.useRef(null);
  const cameraStreamRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const recordingTimerRef = React.useRef(null);
  const recordedChunksRef = React.useRef([]);
  const canvasRef = React.useRef(null);
  const imagePreviewsRef = React.useRef({});
  const videoPreviewsRef = React.useRef({});
  const attachPreviewsRef = React.useRef({});
  const filePayloadTokenRef = React.useRef({});
  const mediaObjectUrlsRef = React.useRef(new Map());
  const recognitionRef = React.useRef(null);
  const attachInputRefs = React.useRef({});
  const imageInputRefs = React.useRef({});
  const resizeRafRef = React.useRef(new WeakMap());
  const rootRef = React.useRef(null);
  const activeCameraFieldRef = React.useRef(null);
  const recordingFieldRef = React.useRef(null);
  const dictationFieldRef = React.useRef(null);
  const cameraRequestIdRef = React.useRef(0);
  React.useEffect(() => {
    const nextProgramacion = mergeProgramacion(defaultProgramacion, normalizeSchedulingValue(scheduling_value));
    const nextKey = JSON.stringify(nextProgramacion);
    if (programacionKeyRef.current !== nextKey) {
      programacionKeyRef.current = nextKey;
      setInternalProgramacion(nextProgramacion);
    }
  }, [defaultProgramacion, scheduling_value]);
  React.useEffect(() => {
    const nextRules = normalizeNotificationRules(notification_value);
    const nextKey = JSON.stringify(nextRules);
    if (notificacionKeyRef.current !== nextKey) {
      notificacionKeyRef.current = nextKey;
      setNotificacionRules(nextRules);
    }
  }, [notification_value]);
  const reminderLabel = React.useMemo(
    () => formatReminderLabel(hasScheduledTime ? scheduled_time : undefined, postponeMinutes),
    [hasScheduledTime, scheduled_time, postponeMinutes]
  );
  const dayLabel = reminderLabel.dayLabel;
  const timeLabel = reminderLabel.timeLabel;
  const scheduleLine = hasScheduledTime ? (
    <p className="text-sm text-slate-500">
      <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
        <IconBell className="h-4 w-4 text-red-500" />
        <span>
          {dayLabel} {timeLabel} hs{postponeMinutes > 0 ? " " : ""}
          {postponeMinutes > 0 ? <span className="text-red-500">+{postponeMinutes} minutos</span> : null}
        </span>
      </span>
    </p>
  ) : null;
  const supportsDictation = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const showProgramacion = String(scheduling).toLowerCase() === "true" && !isReadOnly;
  const showNotificacionWithFieldSelect = showNotificacion;
  const useMobileNotifications = Boolean(forceMobileNotifications);
  const effectiveProgramacionValue = internalProgramacion;
  const handleProgramacionChange = React.useCallback(
    (nextValue) => {
      const nextKey = JSON.stringify(nextValue || {});
      programacionKeyRef.current = nextKey;
      setInternalProgramacion(nextValue);
      if (typeof onProgramacionChange === "function") {
        onProgramacionChange(nextValue);
      }
    },
    [onProgramacionChange]
  );
  const setNotificacionRulesWithCallback = React.useCallback(
    (updater) => {
      setNotificacionRules((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const safeNext = Array.isArray(next) ? next : [];
        const nextKey = JSON.stringify(safeNext);
        notificacionKeyRef.current = nextKey;
        if (typeof onNotificacionRulesChange === "function") {
          onNotificacionRulesChange(safeNext);
        }
        return safeNext;
      });
    },
    [onNotificacionRulesChange]
  );
  function resetFormState() {
    stopDictation();
    if (activeCameraFieldRef.current) {
      closeCamera(activeCameraFieldRef.current);
    }
    stopRecording();
    setValues(initialValues);
    set_file_payloads({});
    handleProgramacionChange(defaultProgramacion);
    setNotificacionRulesWithCallback([]);
    setViewer({ open: false, type: "", src: "", caption: "" });
    setViewerImageError(false);
    setOpenSelectField(null);
    setDictationError({ field: null, message: "" });
    setDictationPhase("idle");
    setDictationPreview({ field: null, text: "" });
    setActiveCameraField(null);
    setCameraError({ field: null, message: "" });
    setRecordingField(null);
    setRecordingSecondsLeft(0);
    const imgPrev = imagePreviewsRef.current;
    for (const key in imgPrev) {
      if (imgPrev[key] && imgPrev[key].url) {
        URL.revokeObjectURL(imgPrev[key].url);
      }
    }
    const vidPrev = videoPreviewsRef.current;
    for (const key in vidPrev) {
      if (vidPrev[key] && vidPrev[key].url) {
        URL.revokeObjectURL(vidPrev[key].url);
      }
    }
    const attPrev = attachPreviewsRef.current;
    for (const key in attPrev) {
      if (attPrev[key] && attPrev[key].url) {
        URL.revokeObjectURL(attPrev[key].url);
      }
    }
    imagePreviewsRef.current = {};
    videoPreviewsRef.current = {};
    attachPreviewsRef.current = {};
    setImagePreviews({});
    setVideoPreviews({});
    setAttachPreviews({});
    const attachInputs = attachInputRefs.current;
    for (const key in attachInputs) {
      if (attachInputs[key]) {
        attachInputs[key].value = "";
      }
    }
    const imageInputs = imageInputRefs.current;
    for (const key in imageInputs) {
      if (imageInputs[key]) {
        imageInputs[key].value = "";
      }
    }
  }
  
  React.useEffect(() => {
    if (hasResultValue) {
      setValues(initialValues);
      set_file_payloads({});
      return;
    }
    if (isBuilderMode) {
      setValues(initialValues);
      return;
    }
    setValues((prev) => ({ ...initialValues, ...prev }));
  }, [initialValues, hasResultValue, isBuilderMode]);
  React.useEffect(() => {
    if (!viewer.open || viewer.type !== "image") {
      setViewerImageError(false);
      return;
    }
    setViewerImageError(false);
  }, [viewer.open, viewer.type, viewer.src]);
  React.useEffect(() => {
    activeCameraFieldRef.current = activeCameraField;
  }, [activeCameraField]);
  
  React.useEffect(() => {
    recordingFieldRef.current = recordingField;
  }, [recordingField]);
  
  React.useEffect(() => {
    dictationFieldRef.current = dictationField;
  }, [dictationField]);
  React.useEffect(() => {
    if (isReadOnly && dictationField) {
      stopDictation();
    }
  }, [isReadOnly, dictationField]);
  React.useEffect(() => {
    function handleOutsideClick(event) {
      if (!openSelectField) return;
      const target = event.target;
      if (target && target.closest && target.closest("[data-select-root]")) return;
      setOpenSelectField(null);
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openSelectField]);
  React.useEffect(() => {
    if (!tabConfig.hasTabs) {
      if (activeTab !== "") {
        setActiveTab("");
      }
      return;
    }
    if (tabConfig.order.indexOf(activeTab) === -1) {
      setActiveTab(tabConfig.order[0]);
    }
  }, [tabConfig, activeTab]);
  React.useEffect(() => {
    if (!submitMode) return;
    const root = rootRef.current;
    if (!root) return;
    const form = findClosestForm(root);
    if (!form) return;
    function onReset() {
      resetFormState();
    }
    form.addEventListener("reset", onReset);
    return () => form.removeEventListener("reset", onReset);
  }, [submitMode, initialValues, defaultProgramacion]);
  function notifyBuilderValueChange(name, value) {
    if (!isBuilderMode || typeof onBuilderFieldValueChange !== "function") return;
    const fieldId = builderFieldIdByName[name];
    if (!fieldId) return;
    onBuilderFieldValueChange(fieldId, value);
  }
  function setFieldValue(name, value) {
    if (isReadOnly) return;
    setValues((prev) => ({ ...prev, [name]: value }));
    notifyBuilderValueChange(name, value);
  }
  function setImageValue(name, file) {
    if (isReadOnly) return;
    setValues((prev) => ({ ...prev, [name]: file }));
    setFilePayload(name, file);
    notifyBuilderValueChange(name, serializeFileValue(file));
    setImagePreviews((prev) => {
      const next = { ...prev };
      if (next[name] && next[name].url) {
        URL.revokeObjectURL(next[name].url);
      }
      if (file) {
        next[name] = { url: URL.createObjectURL(file) };
      } else {
        delete next[name];
      }
      imagePreviewsRef.current = next;
      return next;
    });
  }
  function setVideoValue(name, file) {
    if (isReadOnly) return;
    setValues((prev) => ({ ...prev, [name]: file }));
    setFilePayload(name, file);
    notifyBuilderValueChange(name, serializeFileValue(file));
    setVideoPreviews((prev) => {
      const next = { ...prev };
      if (next[name] && next[name].url) {
        URL.revokeObjectURL(next[name].url);
      }
      if (file) {
        next[name] = { url: URL.createObjectURL(file) };
      } else {
        delete next[name];
      }
      videoPreviewsRef.current = next;
      return next;
    });
  }
  function setAttachValue(name, file) {
    if (isReadOnly) return;
    setValues((prev) => ({ ...prev, [name]: file }));
    setFilePayload(name, file);
    notifyBuilderValueChange(name, serializeFileValue(file));
    setAttachPreviews((prev) => {
      const next = { ...prev };
      if (next[name] && next[name].url) {
        URL.revokeObjectURL(next[name].url);
      }
      if (file) {
        next[name] = { url: URL.createObjectURL(file) };
      } else {
        delete next[name];
      }
      attachPreviewsRef.current = next;
      return next;
    });
  }
  function clearAttachInput(name) {
    const input = attachInputRefs.current[name];
    if (input) {
      input.value = "";
    }
  }
  function stopStream(stream) {
    if (!stream) return;
    stream.getTracks().forEach((track) => {
      track.enabled = false;
      track.stop();
      if (track.readyState !== "ended") {
        setTimeout(() => {
          try {
            track.stop();
          } catch (err) {
            // no-op
          }
        }, 200);
      }
    });
  }
  React.useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        stopStream(cameraStreamRef.current);
        cameraStreamRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          // no-op
        }
        recognitionRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      Object.values(imagePreviewsRef.current).forEach((item) => {
        if (item && item.url) URL.revokeObjectURL(item.url);
      });
      Object.values(videoPreviewsRef.current).forEach((item) => {
        if (item && item.url) URL.revokeObjectURL(item.url);
      });
      Object.values(attachPreviewsRef.current).forEach((item) => {
        if (item && item.url) URL.revokeObjectURL(item.url);
      });
      mediaObjectUrlsRef.current.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      mediaObjectUrlsRef.current.clear();
    };
  }, []);
  function getDefaultValue(field) {
    if (!field) return "";
    if (VALUE_FIELD_TYPES.has(field.type) && Object.prototype.hasOwnProperty.call(field, "value")) {
      const rawValue = field.value;
      if (rawValue !== undefined && rawValue !== null) {
        if (field.type === "bool") return isTrueValue(rawValue);
        return rawValue;
      }
    }
    switch (field.type) {
      case "bool":
        return false;
      case "image":
      case "video":
      case "attach":
        return null;
      case "rating":
        return 0;
      case "multiplelist":
        return [];
      case "subtitle":
        return "";
      case "pain":
      case "selector":
      case "singlelist":
      case "memo":
        return "";
      case "htmlview":
        return 0;
      case "videoview":
        return "";
      case "slider": {
        const opts = Array.isArray(field.options) ? field.options : [];
        return opts.length ? opts[0] : "";
      }
      default:
        return "";
    }
  }
  function parseResultValue(input) {
    if (input === undefined || input === null) return null;
    if (typeof input === "string") {
      const trimmed = input.trim();
      if (!trimmed) return null;
      try {
        return JSON.parse(trimmed);
      } catch (err) {
        return {};
      }
    }
    if (typeof input === "object") return input;
    return null;
  }
  function isRangeField(field) {
    if (!field) return false;
    if (!["number", "date", "time", "datetime"].includes(field.type)) return false;
    return field.minValue !== undefined || field.maxValue !== undefined;
  }
  function parseDateToMs(value) {
    if (typeof value !== "string") return null;
    const parts = value.split("-");
    if (parts.length !== 3) return null;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    return new Date(year, month - 1, day).getTime();
  }
  function parseTimeToMinutes(value) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value !== "string") return null;
    const parts = value.split(":").map((part) => Number(part));
    if (parts.length < 2 || parts.some((part) => !Number.isFinite(part))) return null;
    const [hours, minutes, seconds = 0] = parts;
    return hours * 60 + minutes + seconds / 60;
  }
  function parseDateTimeToMs(value) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value !== "string") return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.getTime();
  }
  function getFieldNumericValue(field, value) {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    switch (field.type) {
      case "number": {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      case "date":
        return parseDateToMs(String(value));
      case "time":
        return parseTimeToMinutes(value);
      case "datetime":
        return parseDateTimeToMs(String(value));
      default:
        return null;
    }
  }
  function getRangeBoundary(field, boundaryValue) {
    if (boundaryValue === undefined || boundaryValue === null || boundaryValue === "") return null;
    if (typeof boundaryValue === "number" && Number.isFinite(boundaryValue)) return boundaryValue;
    switch (field.type) {
      case "number": {
        const parsed = Number(boundaryValue);
        return Number.isFinite(parsed) ? parsed : null;
      }
      case "date":
        return parseDateToMs(String(boundaryValue));
      case "time":
        return parseTimeToMinutes(boundaryValue);
      case "datetime":
        return parseDateTimeToMs(String(boundaryValue));
      default:
        return null;
    }
  }
  function isOutOfRange(field, value) {
    const numericValue = getFieldNumericValue(field, value);
    if (numericValue === null) return false;
    const min = getRangeBoundary(field, field.minValue);
    const max = getRangeBoundary(field, field.maxValue);
    if (min !== null && numericValue < min) return true;
    if (max !== null && numericValue > max) return true;
    return false;
  }
  function isFieldComplete(field, value) {
    if (isControlField(field)) return true;
    if (
      field.type === "title" ||
      field.type === "subtitle" ||
      field.type === "textView" ||
      field.type === "htmlview" ||
      field.type === "videoview" ||
      field.type === "divider" ||
      field.type === "code"
    )
      return true;
    if (field.type === "imageview") {
      return getImageViewOptions(field).length > 0;
    }
    if (field.type === "bool") return Boolean(value);
    if (field.type === "rating") return Number(value) >= 1;
    if (field.type === "pain") return getSelectedIndex(value, PAIN_KEYS) >= 0;
    if (field.type === "selector") return getSelectedIndex(value, getSelectorOptions(field)) >= 0;
    if (field.type === "multiplelist") return Array.isArray(value) && value.length > 0;
    if (field.type === "slider") return value !== undefined && value !== null && String(value).trim() !== "";
    if (field.type === "attach" || field.type === "image" || field.type === "video") return Boolean(value);
    if (field.type === "options" || field.type === "singlelist") return value !== undefined && value !== null && String(value).trim() !== "";
    if (field.type === "date" || field.type === "time" || field.type === "datetime" || field.type === "number" || field.type === "text" || field.type === "memo") {
      return value !== undefined && value !== null && String(value).trim() !== "";
    }
    return value !== undefined && value !== null && String(value).trim() !== "";
  }
  function serializeFileValue(file) {
    if (!file) return null;
    if (typeof file === "string") return file;
    if (typeof file === "object") {
      if (typeof File !== "undefined" && file instanceof File) {
        const name = typeof file.name === "string" ? file.name : "";
        const size = typeof file.size === "number" ? file.size : undefined;
        const type = typeof file.type === "string" ? file.type : "";
        return { name, size, type };
      }
      return file;
    }
    return file;
  }
  function buildResultPayload(fieldsList, currentValues, filePayloads) {
    const payload = {};
    for (const field of fieldsList) {
      if (!field || !field.name) continue;
      if (isControlField(field) || field.type === "adder") continue;
      const value = currentValues[field.name];
      switch (field.type) {
        case "title":
        case "subtitle":
        case "textView":
        case "htmlview":
          payload[field.name] = field.html || field.text || field.value || "";
          break;
        case "videoview":
          payload[field.name] = field.youtube || field.url || field.value || "";
          break;
        case "divider":
          payload[field.name] = null;
          break;
        case "imageview":
          payload[field.name] = getImageViewOptions(field);
          break;
        case "bool":
          payload[field.name] = Boolean(value);
          break;
        case "rating":
          payload[field.name] = {
            value: Number(value) || 0,
            comment: String(coalesce(currentValues[getRatingCommentKey(field.name)], "")),
          };
          break;
        case "pain": {
          const idx = getSelectedIndex(value, PAIN_KEYS);
          payload[field.name] = idx >= 0 ? idx + 1 : 0;
          break;
        }
        case "selector": {
          const options = getSelectorOptions(field);
          const idx = getSelectedIndex(value, options);
          payload[field.name] = idx >= 0 ? idx + 1 : 0;
          break;
        }
        case "multiplelist":
          payload[field.name] = Array.isArray(value) ? value : [];
          break;
        case "image":
        case "video":
        case "attach":
          payload[field.name] = coalesce(filePayloads[field.name], serializeFileValue(value));
          break;
        default:
          payload[field.name] = coalesce(value, "");
          break;
      }
    }
    return payload;
  }
  const resultPayload = React.useMemo(() => buildResultPayload(fields, values, file_payloads), [fields, values, file_payloads]);
  function setResult(nextOperation) {
    setOperation(nextOperation);
  }
  function normalizeExtensions(extensions) {
    if (!extensions) return undefined;
    const list = Array.isArray(extensions) ? extensions : String(extensions).split(",");
    const cleaned = list
      .map((item) => String(item).trim())
      .filter((item) => item.length > 0)
      .map((item) => (item.startsWith(".") ? item : `.${item}`));
    return cleaned.length ? cleaned.join(",") : undefined;
  }
  function getRecordingSeconds(field) {
    const n = Number(field.seconds);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 5;
  }
  function getSupportedRecorderType() {
    if (!window.MediaRecorder) return "";
    const types = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm", "video/mp4"];
    return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
  }
  function clearRecordingTimer() {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }
  function stopRecording() {
    clearRecordingTimer();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }
    setRecordingField(null);
    setRecordingSecondsLeft(0);
    mediaRecorderRef.current = null;
  }
  function commitDictationPreview(fieldName) {
    if (!fieldName) return;
    if (!dictationPreview || dictationPreview.field !== fieldName) return;
    const previewText = String(dictationPreview.text || "").trim();
    if (!previewText) return;
    const normalized = applyDictationPunctuation(previewText);
    if (!normalized) return;
    setValues((prev) => {
      const rawCurrent = prev[fieldName];
      const current = rawCurrent === 0 ? "" : String(coalesce(rawCurrent, ""));
      let nextValue = appendDictationText(current, normalized);
      if (nextValue.length >= MAX_DICTATION_CHARS) {
        nextValue = nextValue.slice(0, MAX_DICTATION_CHARS);
        setDictationError({ field: fieldName, message: "Se alcanzó el límite de 50.000 caracteres." });
      }
      return { ...prev, [fieldName]: nextValue };
    });
    setDictationPreview({ field: null, text: "" });
  }
  function stopDictation() {
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.onend = null;
      recognition.onerror = null;
      try {
        recognition.stop();
      } catch (err) {
        // no-op
      }
    }
    recognitionRef.current = null;
    setDictationField(null);
    setDictationPhase("idle");
    setDictationPreview({ field: null, text: "" });
  }
  function startDictation(fieldName) {
    if (isReadOnly) return;
    if (!supportsDictation) {
      setDictationError({ field: fieldName, message: "Dictado no disponible en este navegador." });
      return;
    }
    if (dictationFieldRef.current && dictationFieldRef.current !== fieldName) {
      stopDictation();
    }
    if (dictationFieldRef.current === fieldName) {
      commitDictationPreview(fieldName);
      stopDictation();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "es-ES";
    recognition.interimResults = true;
    recognition.continuous = true;
    recognitionRef.current = recognition;
    setDictationField(fieldName);
    setDictationError({ field: null, message: "" });
    setDictationPhase("listening");
    setDictationPreview({ field: fieldName, text: "" });
    recognition.onaudiostart = () => {
      if (dictationFieldRef.current === fieldName) setDictationPhase("listening");
    };
    recognition.onspeechstart = () => {
      if (dictationFieldRef.current === fieldName) setDictationPhase("listening");
    };
    recognition.onspeechend = () => {
      if (dictationFieldRef.current === fieldName) setDictationPhase("processing");
    };
    recognition.onaudioend = () => {
      if (dictationFieldRef.current === fieldName) setDictationPhase("processing");
    };
    recognition.onresult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0] && result[0].transcript ? result[0].transcript : "";
        } else {
          interimTranscript += result[0] && result[0].transcript ? result[0].transcript : "";
        }
      }
      if (dictationFieldRef.current === fieldName) {
        setDictationPreview({ field: fieldName, text: interimTranscript.trim() });
      }
      const normalizedTranscript = applyDictationPunctuation(finalTranscript.trim());
      if (!normalizedTranscript) return;
      setValues((prev) => {
        const rawCurrent = prev[fieldName];
        const current = rawCurrent === 0 ? "" : String(coalesce(rawCurrent, ""));
        let nextValue = appendDictationText(current, normalizedTranscript);
        if (nextValue.length >= MAX_DICTATION_CHARS) {
          nextValue = nextValue.slice(0, MAX_DICTATION_CHARS);
          setDictationError({ field: fieldName, message: "Se alcanzó el límite de 50.000 caracteres." });
          stopDictation();
        }
        return { ...prev, [fieldName]: nextValue };
      });
      if (dictationFieldRef.current === fieldName) {
        setDictationPreview({ field: fieldName, text: "" });
        setDictationPhase("listening");
      }
    };
    recognition.onerror = () => {
      setDictationError({ field: fieldName, message: "No se pudo iniciar el dictado. Verifique permisos de micrófono." });
      stopDictation();
    };
    recognition.onend = () => {
      if (dictationFieldRef.current === fieldName) {
        recognitionRef.current = null;
        setDictationField(null);
        setDictationPhase("idle");
      }
    };
    recognition.start();
  }
  function openCamera(fieldName, facingMode = cameraFacing, includeAudio = false) {
    if (isReadOnly) return Promise.resolve(null);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError({ field: fieldName, message: "La cámara no está disponible en este navegador." });
      return Promise.resolve(null);
    }
  
    const requestId = ++cameraRequestIdRef.current;
    setActiveCameraField(fieldName);
    activeCameraFieldRef.current = fieldName;
    setCameraFacing(facingMode);
  
    if (cameraStreamRef.current) {
      stopStream(cameraStreamRef.current);
      cameraStreamRef.current = null;
    }
  
    const existingVideo = videoRef.current;
    if (existingVideo && existingVideo.srcObject) {
      stopStream(existingVideo.srcObject);
      existingVideo.srcObject = null;
      if (existingVideo.pause) existingVideo.pause();
      if (existingVideo.load) existingVideo.load();
    }
  
    return navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: facingMode } }, audio: includeAudio })
      .then((stream) => {
        if (cameraRequestIdRef.current !== requestId || activeCameraFieldRef.current !== fieldName) {
          stopStream(stream);
          return null;
        }
        setCameraError({ field: null, message: "" });
        cameraStreamRef.current = stream;
  
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          if (video.play) video.play();
        }
        return stream;
      })
      .catch((err) => {
        setCameraError({ field: fieldName, message: getCameraErrorMessage(err, includeAudio) });
        setActiveCameraField(null);
        return null;
      });
  }
  
  function startRecordingWithStream(field, stream, seconds, recorderType) {
    if (!stream) return;
    recordedChunksRef.current = [];
    const recorder = new MediaRecorder(stream, recorderType ? { mimeType: recorderType } : undefined);
    mediaRecorderRef.current = recorder;
    setRecordingField(field.name);
    setRecordingSecondsLeft(seconds);
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: recorder.mimeType || "video/webm" });
      const ext = recorder.mimeType && recorder.mimeType.indexOf("mp4") !== -1 ? "mp4" : "webm";
      const file = new File([blob], `${field.name}.${ext}`, { type: recorder.mimeType || "video/webm" });
      setVideoValue(field.name, file);
      recordedChunksRef.current = [];
      setRecordingField(null);
      setRecordingSecondsLeft(0);
      mediaRecorderRef.current = null;
      closeCamera(field.name);
    };
    recorder.start();
    clearRecordingTimer();
    recordingTimerRef.current = setInterval(() => {
      setRecordingSecondsLeft((prev) => {
        if (prev <= 1) {
          stopRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }
  async function startVideoRecording(field) {
    if (isReadOnly) return;
    if (recordingFieldRef.current === field.name) {
      stopRecording();
      return;
    }
    if (!window.MediaRecorder) {
      setCameraError({ field: field.name, message: "La grabación de video no está disponible en este navegador." });
      return;
    }
    const seconds = getRecordingSeconds(field);
    const recorderType = getSupportedRecorderType();
    let stream = cameraStreamRef.current;
    const hasAudio = stream && stream.getAudioTracks && stream.getAudioTracks().length > 0;
    if (!stream || activeCameraFieldRef.current !== field.name || !hasAudio) {
      stream = await openCamera(field.name, cameraFacing, true);
    }
    if (!stream) return;
    startRecordingWithStream(field, stream, seconds, recorderType);
  }
  function closeCamera(fieldName) {
    if (!fieldName) return;
    cameraRequestIdRef.current += 1;
    activeCameraFieldRef.current = null;
    if (recordingFieldRef.current === fieldName) {
      stopRecording();
    }
    if (cameraStreamRef.current) {
      stopStream(cameraStreamRef.current);
      cameraStreamRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      if (video.srcObject) {
        stopStream(video.srcObject);
        video.srcObject = null;
      }
      if (video.pause) {
        video.pause();
      }
      if (video.load) {
        video.load();
      }
    }
    setActiveCameraField(null);
  }
  function toggleCamera(fieldName, includeAudio = false) {
    const nextFacing = cameraFacing === "environment" ? "user" : "environment";
    openCamera(fieldName, nextFacing, includeAudio);
  }
  function capturePhoto(fieldName) {
    if (isReadOnly) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `${fieldName}.jpg`, { type: "image/jpeg" });
      setImageValue(fieldName, file);
      closeCamera(fieldName);
    }, "image/jpeg", 0.92);
  }
  function toggleMultipleValue(fieldName, option) {
    if (isReadOnly) return;
    const current = Array.isArray(values[fieldName]) ? values[fieldName] : [];
    const next = current.includes(option) ? current.filter((item) => item !== option) : [...current, option];
    setFieldValue(fieldName, next);
  }
  function openViewer(type, src, caption = "") {
    if (!src) return;
    setViewer({ open: true, type, src, caption });
  }
  function closeViewer() {
    setViewer({ open: false, type: "", src: "", caption: "" });
  }
  function getMediaStreamFromValue(value) {
    if (!value) return null;
    if (typeof MediaStream !== "undefined" && value instanceof MediaStream) return value;
    if (typeof value === "object") {
      const candidate = value.stream || value.srcObject || value.mediaStream;
      if (typeof MediaStream !== "undefined" && candidate instanceof MediaStream) return candidate;
    }
    return null;
  }
  function getMediaObjectUrl(value) {
    if (!value || typeof value !== "object") return "";
    if (typeof Blob === "undefined") return "";
    if (!(value instanceof Blob)) return "";
    const cache = mediaObjectUrlsRef.current;
    if (cache.has(value)) return cache.get(value) || "";
    const url = URL.createObjectURL(value);
    cache.set(value, url);
    return url;
  }
  function getMediaPreviewSrc(value) {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      const dataUrl = getDataUrlFromValue(value);
      if (dataUrl) return dataUrl;
      const candidates = ["url", "href", "src", "link", "path", "downloadUrl", "previewUrl", "uri"];
      for (const key of candidates) {
        if (typeof value[key] === "string" && value[key].trim()) {
          return value[key];
        }
      }
    }
    return "";
  }
  function getDataUrlFromValue(value) {
    if (!value || typeof value !== "object") return "";
    const candidates = ["dataUrl", "dataURL", "data_url", "base64", "b64", "content"];
    for (const key of candidates) {
      if (typeof value[key] !== "string") continue;
      const raw = value[key].trim();
      if (!raw) continue;
      if (raw.startsWith("data:")) return raw;
      const mime = typeof value.type === "string" && value.type ? value.type : "application/octet-stream";
      return `data:${mime};base64,${raw}`;
    }
    return "";
  }
  function shouldUseExternalViewer(fileUrl) {
    if (!fileUrl || typeof fileUrl !== "string") return false;
    if (fileUrl.startsWith("blob:") || fileUrl.startsWith("data:")) return false;
    try {
      const parsed = new URL(fileUrl);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
      if (["localhost", "127.0.0.1", "0.0.0.0"].includes(parsed.hostname)) return false;
      const ext = getUrlExtension(parsed.pathname);
      if (ext === "pdf") return false;
      if (IMAGE_EXTENSIONS.has(ext)) return false;
    } catch (err) {
      return false;
    }
    return true;
  }
  function buildExternalViewerUrl(fileUrl) {
    if (!fileUrl) return "";
    const encoded = encodeURIComponent(fileUrl);
    if (ATTACH_VIEWER_URL.includes("{url}")) return ATTACH_VIEWER_URL.replaceAll("{url}", encoded);
    if (ATTACH_VIEWER_URL.includes("%7Burl%7D")) return ATTACH_VIEWER_URL.replaceAll("%7Burl%7D", encoded);
    const joiner = ATTACH_VIEWER_URL.includes("?") ? "&" : "?";
    return `${ATTACH_VIEWER_URL}${joiner}url=${encoded}`;
  }
  function getAttachViewerSrc(fileUrl) {
    if (!fileUrl) return "";
    return shouldUseExternalViewer(fileUrl) ? buildExternalViewerUrl(fileUrl) : fileUrl;
  }
  function getUrlExtension(pathname) {
    if (!pathname) return "";
    const cleaned = pathname.split("?")[0].split("#")[0];
    const lastDot = cleaned.lastIndexOf(".");
    if (lastDot === -1) return "";
    return cleaned.slice(lastDot + 1).toLowerCase();
  }
  function getYouTubeId(input) {
    if (typeof input !== "string") return "";
    const trimmed = input.trim();
    if (!trimmed) return "";
    try {
      const url = new URL(trimmed);
      const host = url.hostname.replace(/^www\./i, "");
      if (host === "youtu.be") {
        return url.pathname.replace(/^\/+/, "").split("/")[0] || "";
      }
      if (!host.endsWith("youtube.com")) return "";
      const vParam = url.searchParams.get("v");
      if (vParam) return vParam;
      const parts = url.pathname.split("/").filter(Boolean);
      const embedIndex = parts.indexOf("embed");
      if (embedIndex >= 0 && parts[embedIndex + 1]) return parts[embedIndex + 1];
      const shortsIndex = parts.indexOf("shorts");
      if (shortsIndex >= 0 && parts[shortsIndex + 1]) return parts[shortsIndex + 1];
      const vIndex = parts.indexOf("v");
      if (vIndex >= 0 && parts[vIndex + 1]) return parts[vIndex + 1];
    } catch (err) {
      return "";
    }
    return "";
  }
  function buildYouTubeEmbedUrl(input) {
    const id = getYouTubeId(input);
    return id ? `https://www.youtube.com/embed/${id}` : "";
  }
  function getCameraErrorMessage(err, includeAudio) {
    const name = err && err.name ? err.name : "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      return includeAudio ? "Permiso denegado para cámara o micrófono." : "Permiso denegado para cámara.";
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      return includeAudio ? "No se encontró cámara o micrófono." : "No se encontró cámara.";
    }
    if (name === "NotReadableError" || name === "TrackStartError") {
      return includeAudio ? "No se pudo iniciar cámara o micrófono. Puede estar en uso." : "No se pudo iniciar la cámara. Puede estar en uso.";
    }
    if (name === "OverconstrainedError") {
      return "No se pudo acceder a la cámara con la configuración solicitada.";
    }
    return includeAudio ? "No se pudo acceder a cámara o micrófono. Verifique permisos." : "No se pudo acceder a la cámara. Verifique permisos.";
  }
  
  function addNotificacionRule() {
    const nextRule = { op: "==", constant: "", color: "red" };
    if (inputFieldOptions.length) {
      nextRule.field = inputFieldOptions[0].value;
    }
    setNotificacionRulesWithCallback((prev) => [...prev, nextRule]);
  }
  function handleAddNotificacionRuleClick(event) {
    if (event && event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (typeof window !== "undefined") {
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      addNotificacionRule();
      window.requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
      return;
    }
    addNotificacionRule();
  }
  function removeNotificacionRule(index) {
    setNotificacionRulesWithCallback((prev) => prev.filter((_, i) => i !== index));
  }
  function updateNotificacionRule(index, patch) {
    setNotificacionRulesWithCallback((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  }
  function renderNotificacionSiConCampo(fieldOptions) {
    const hasRules = notificacionRules.length > 0;
    const ruleGridClass = useMobileNotifications
      ? "grid grid-cols-1 gap-2 text-sm"
      : "grid grid-cols-1 gap-2 text-sm sm:grid-cols-[minmax(0,1fr)_110px_minmax(0,1fr)_110px_32px] sm:items-center";
    const ruleOpWrapperClass = useMobileNotifications ? "grid grid-cols-[110px_minmax(0,1fr)] gap-2" : "grid grid-cols-[110px_minmax(0,1fr)] gap-2 sm:contents";
    const ruleColorWrapperClass = useMobileNotifications ? "flex w-full items-center gap-2" : "flex w-full items-center gap-2 sm:contents";
    return (
      <div className="mt-3 rounded-xl border border-slate-200 p-3">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Reglas de notificación</h3>
        <div className="space-y-2" style={{ overflowAnchor: "none" }}>
          {notificacionRules.map((rule, index) => (
            <React.Fragment key={index}>
              <div className={ruleGridClass}>
                <NotificacionFieldSelect
                  options={fieldOptions}
                  value={rule.field}
                  onChange={(next) => updateNotificacionRule(index, { field: next })}
                  surfaceBgClass={surfaceBgClass}
                  surfaceHoverClass={surfaceHoverClass}
                />
                <div className={ruleOpWrapperClass}>
                  <NotificacionOpSelect
                    value={rule.op}
                    onChange={(nextOp) => {
                      const isTrend = nextOp === "Tend";
                      const nextConstant = isTrend
                        ? NOTIFICATION_TREND_OPTIONS.some((opt) => opt.value === rule.constant)
                          ? rule.constant
                          : "normal"
                        : NOTIFICATION_TREND_OPTIONS.some((opt) => opt.value === rule.constant)
                          ? ""
                          : rule.constant;
                      updateNotificacionRule(index, { op: nextOp, constant: nextConstant });
                    }}
                    surfaceBgClass={surfaceBgClass}
                    surfaceHoverClass={surfaceHoverClass}
                  />
                  {rule.op === "Tend" ? (
                    <select
                      value={rule.constant || "normal"}
                      onChange={(e) => updateNotificacionRule(index, { constant: e.target.value })}
                      className={`h-9 w-full rounded-lg ${surfaceBgClass} px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
                    >
                      {NOTIFICATION_TREND_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      placeholder="Constante"
                      value={rule.constant}
                      onChange={(e) => updateNotificacionRule(index, { constant: e.target.value })}
                      readOnly={isReadOnly}
                      className={`h-9 w-full rounded-lg ${surfaceBgClass} px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
                    />
                  )}
                </div>
                <div className={ruleColorWrapperClass}>
                  <div className="flex-1 sm:w-full">
                    <NotificacionColorSelect
                      value={rule.color}
                      onChange={(next) => updateNotificacionRule(index, { color: next })}
                      options={NOTIFICATION_COLOR_OPTIONS}
                      surfaceBgClass={surfaceBgClass}
                      useMobileNotifications={useMobileNotifications}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeNotificacionRule(index)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-red-500 ring-1 ring-slate-200 hover:bg-red-50"
                    aria-label="Eliminar regla"
                  >
                    ✕
                  </button>
                </div>
              </div>
              {index < notificacionRules.length - 1 ? <div className="h-px bg-slate-200" /> : null}
            </React.Fragment>
          ))}
        </div>
        <button
          type="button"
          onClick={handleAddNotificacionRuleClick}
          className={`${hasRules ? "mt-3" : "mt-1"} inline-flex items-center justify-center rounded-full ${surfaceBgStrongClass} px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass}`}
        >
          Agregar regla
        </button>
      </div>
    );
  }
  function formatFileLabel(value) {
    if (!value) return "Sin archivo";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      const name = typeof value.name === "string" && value.name ? value.name : "Archivo adjunto";
      const type = typeof value.type === "string" && value.type ? ` (${value.type})` : "";
      const size = typeof value.size === "number" ? ` - ${value.size} bytes` : "";
      return `${name}${type}${size}`;
    }
    return String(value);
  }
  function isGroupCloneEligible(field) {
    if (!field) return false;
    if (field.type === "adder") return false;
    if (field.type === "imageview" || field.type === "videoview") {
      return false;
    }
    if (
      field.type === "layout-1" ||
      field.type === "layout-2" ||
      field.type === "layout-3" ||
      field.type === "layout-free" ||
      field.type === "tab"
    ) {
      return true;
    }
    if (
      field.type === "title" ||
      field.type === "subtitle" ||
      field.type === "textView" ||
      field.type === "htmlview" ||
      field.type === "divider"
    ) {
      return true;
    }
    return Boolean(field.name);
  }
  function getGroupValue(value) {
    if (value === undefined || value === null) return "";
    const trimmed = String(value).trim();
    return trimmed;
  }
  function getNextCloneIndex(baseName, fieldsList, cache) {
    if (!baseName) return 1;
    if (cache.has(baseName)) {
      const nextIndex = cache.get(baseName) + 1;
      cache.set(baseName, nextIndex);
      return nextIndex;
    }
    let maxIndex = 0;
    const pattern = new RegExp(`^${escapeRegExp(baseName)}_(\\d+)$`);
    for (const field of fieldsList) {
      if (!field || !field.name) continue;
      if (field.name === baseName) {
        if (maxIndex < 1) maxIndex = 1;
      }
      const match = field.name.match(pattern);
      if (match) {
        const idx = Number(match[1]);
        if (Number.isFinite(idx) && idx > maxIndex) maxIndex = idx;
      }
    }
    const nextIndex = Math.max(2, maxIndex + 1);
    cache.set(baseName, nextIndex);
    return nextIndex;
  }
  function cloneFieldsForAdder(fieldsList, adderField, groupId) {
    const groupValue = getGroupValue(adderField && adderField.adderGroup);
    if (!groupValue) return null;
    const adderIndex = fieldsList.findIndex(
      (field) => field === adderField || (adderField && adderField._id && field && field._id === adderField._id)
    );
    if (adderIndex === -1) return null;
    const candidates = [];
    for (let i = 0; i < adderIndex; i += 1) {
      const field = fieldsList[i];
      if (!isGroupCloneEligible(field)) continue;
      if (getGroupValue(field.adderGroup) !== groupValue) continue;
      candidates.push(field);
    }
    if (!candidates.length) return null;
    const indexCache = new Map();
    const clones = candidates.map((field) => {
      const baseName = field.name;
      const nextIndex = baseName ? getNextCloneIndex(baseName, fieldsList, indexCache) : null;
      const cloned = {
        ...field,
        __adderClone: true,
        __adderGroupId: groupId,
      };
      if (baseName) {
        cloned.name = `${baseName}_${nextIndex}`;
        cloned.__adderBaseName = baseName;
      }
      if (Object.prototype.hasOwnProperty.call(cloned, "value")) {
        delete cloned.value;
      }
      if (Object.prototype.hasOwnProperty.call(cloned, "adderGroup")) {
        delete cloned.adderGroup;
      }
      if (Object.prototype.hasOwnProperty.call(cloned, "_id")) {
        delete cloned._id;
      }
      return cloned;
    });
    const nextFields = [...fieldsList];
    const layoutStart = { type: "layout-1", __adderGroupId: groupId };
    const divider = { type: "divider", __adderGroupId: groupId };
    nextFields.splice(adderIndex, 0, layoutStart, divider, ...clones);
    return nextFields;
  }
  function renumberCloneNames(fieldsList) {
    const counters = new Map();
    const renames = [];
    const nextFields = fieldsList.map((field) => {
      if (!field || !field.__adderBaseName) return field;
      const baseName = field.__adderBaseName;
      const nextIndex = (counters.get(baseName) || 1) + 1;
      counters.set(baseName, nextIndex);
      const nextName = `${baseName}_${nextIndex}`;
      if (field.name === nextName) return field;
      renames.push({ from: field.name, to: nextName });
      return { ...field, name: nextName };
    });
    return { fields: nextFields, renames };
  }
  function applyRenamesToValues(renames) {
    if (!renames.length) return;
    setValues((prev) => {
      const next = { ...prev };
      renames.forEach(({ from, to }) => {
        if (!Object.prototype.hasOwnProperty.call(next, from)) return;
        if (!Object.prototype.hasOwnProperty.call(next, to)) {
          next[to] = next[from];
        }
        delete next[from];
      });
      return next;
    });
  }
  function applyRenamesToFilePayloads(renames) {
    if (!renames.length) return;
    set_file_payloads((prev) => {
      const next = { ...prev };
      renames.forEach(({ from, to }) => {
        if (!Object.prototype.hasOwnProperty.call(next, from)) return;
        if (!Object.prototype.hasOwnProperty.call(next, to)) {
          next[to] = next[from];
        }
        delete next[from];
      });
      return next;
    });
  }
  function getCloneDeleteTarget(field, fieldsList) {
    if (!field || !field.__adderGroupId) return null;
    const clones = fieldsList.filter(
      (candidate) =>
        candidate && candidate.__adderClone === true && candidate.__adderGroupId === field.__adderGroupId
    );
    if (!clones.length) return null;
    const first = clones[0];
    let offset = 0;
    if (first.type === "layout-1") {
      offset = 1;
    } else if (first.type === "layout-2") {
      offset = 2;
    } else if (first.type === "layout-3") {
      offset = 3;
    } else if (first.type === "layout-free") {
      const columns = normalizeLayoutFreeOptions(first.options).length;
      offset = Math.max(1, columns);
    }
    const index = Math.min(offset, clones.length - 1);
    return clones[index] || null;
  }
  function shouldShowDeleteForClone(field, fieldsList) {
    if (!field || field.__adderClone !== true) return false;
    const target = getCloneDeleteTarget(field, fieldsList);
    return target === field;
  }
  function handleRemoveAdderGroup(groupId) {
    if (!groupId) return;
    if (isBuilderMode && typeof onBuilderRemoveAdderGroup === "function") {
      onBuilderRemoveAdderGroup(groupId);
      return;
    }
    setDynamicFields((prev) => {
      const filtered = prev.filter((field) => !(field && field.__adderGroupId === groupId));
      const { fields: renumbered, renames } = renumberCloneNames(filtered);
      applyRenamesToValues(renames);
      applyRenamesToFilePayloads(renames);
      return renumbered;
    });
  }
  function handleAdderClick(field) {
    if (isReadOnly) return;
    if (isBuilderMode && typeof onBuilderAdderClone === "function") {
      onBuilderAdderClone(field);
      return;
    }
    setDynamicFields((prev) => {
      const groupId = `adder_${adderGroupIdRef.current}`;
      adderGroupIdRef.current += 1;
      const next = cloneFieldsForAdder(prev, field, groupId);
      return next || prev;
    });
  }
  function isInputField(field) {
    if (!field || !field.name) return false;
    if (isControlField(field)) return false;
    const type = field.type;
    if (!type) return true;
    if (
      type === "title" ||
      type === "textView" ||
      type === "htmlview" ||
      type === "videoview" ||
      type === "divider" ||
      type === "imageview" ||
      type === "selector" ||
      type === "subtitle" ||
      type === "image" ||
      type === "video" ||
      type === "attach" ||
      type === "rating" ||
      type === "adder" ||
      type === "code" ||
      type === "memo" ||
      type === "bool" ||
      type === "date" ||
      type === "time" ||
      type === "datetime"
    ) {
      return false;
    }
    return true;
  }
  function getFieldLabel(field) {
    if (!field) return "Campo";
    if (field.caption) return field.caption;
    if (field.label) return field.label;
    if (field.name) return field.name;
    return "Campo";
  }
  function getBuilderFieldId(field) {
    if (!field) return null;
    if (field._id) return field._id;
    if (field.name) return `name_${field.name}`;
    return null;
  }
  function parseBuilderDragData(event) {
    const raw = event.dataTransfer.getData("text/plain");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }
  function getBuilderFieldIndex(fieldId) {
    if (!fieldId) return -1;
    return fields.findIndex((field) => getBuilderFieldId(field) === fieldId);
  }
  function handleBuilderDrop(event, targetId) {
    const data = parseBuilderDragData(event);
    if (!data) return;
    if (onBuilderDropAt) {
      const targetIndex = targetId ? getBuilderFieldIndex(targetId) : fields.length;
      if (targetId && targetIndex === -1) return;
      onBuilderDropAt(targetIndex, data);
      return;
    }
    if (!onBuilderMoveField || data.source !== "form" || !data.fieldId) return;
    if (targetId && data.fieldId === targetId) return;
    onBuilderMoveField(data.fieldId, targetId || null);
  }
  function renderFieldWithBuilder(field, options = {}) {
    const rendered = renderField(field);
    if (!field || !rendered) return rendered;
    const isAdderClone = Boolean(field.__adderClone);
    const showDelete = shouldShowDeleteForClone(field, fields);
    const deleteButton = showDelete ? (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleRemoveAdderGroup(field.__adderGroupId);
        }}
        onMouseDown={(event) => {
          event.stopPropagation();
          handleRemoveAdderGroup(field.__adderGroupId);
        }}
        className="absolute right-1 top-[-2px] z-20 inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] text-red-500 ring-1 ring-red-200 hover:bg-red-50"
        aria-label="Eliminar grupo"
      >
        ✕
      </button>
    ) : null;
    if (!isBuilderMode) {
      if (!deleteButton) return rendered;
      return (
        <div className="relative">
          {rendered}
          {deleteButton}
        </div>
      );
    }
    const fieldId = getBuilderFieldId(field);
    const isSelected = fieldId && builderSelectedId === fieldId;
    const isDragOver = fieldId && builderDragOverId === fieldId;
    const hideHandle = options.hideHandle === true;
    return (
      <div
        key={fieldId || field.name || field.type}
        data-builder-field="true"
        draggable={hideHandle && !isAdderClone}
        onDragStart={(event) => {
          if (isAdderClone) return;
          if (!hideHandle || !fieldId) return;
          event.dataTransfer.setData("text/plain", JSON.stringify({ source: "form", fieldId }));
          event.dataTransfer.effectAllowed = "move";
        }}
        onClick={() => {
          if (isAdderClone) return;
          if (fieldId && onBuilderSelectField) onBuilderSelectField(fieldId);
        }}
        onDragOver={(event) => {
          if (isAdderClone) return;
          if (!canHandleBuilderDrop) return;
          event.preventDefault();
          event.stopPropagation();
          if (fieldId) setBuilderDragOverId(fieldId);
        }}
        onDragLeave={() => {
          if (isAdderClone) return;
          if (builderDragOverId && builderDragOverId === fieldId) {
            setBuilderDragOverId(null);
          }
        }}
        onDrop={(event) => {
          if (isAdderClone) return;
          event.preventDefault();
          event.stopPropagation();
          handleBuilderDrop(event, fieldId);
          setBuilderDragOverId(null);
        }}
        className={`relative rounded-xl ${
          isSelected ? "ring-1 ring-sky-300 bg-sky-50/60" : ""
        } ${isDragOver ? "bg-sky-50" : ""}`}
      >
        {deleteButton}
        <div className="relative">
          <div className="min-w-0">{rendered}</div>
          {!hideHandle ? (
            <div
              className="absolute top-0 bottom-0 flex items-center justify-center"
              style={{ right: "calc(-1 * var(--builder-controls-width))", width: "var(--builder-controls-width)" }}
            >
              <button
                type="button"
                draggable
                onDragStart={(event) => {
                  if (!fieldId) return;
                  event.dataTransfer.setData("text/plain", JSON.stringify({ source: "form", fieldId }));
                  event.dataTransfer.effectAllowed = "move";
                }}
                onClick={() => {
                  if (fieldId && onBuilderSelectField) onBuilderSelectField(fieldId);
                }}
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-[10px] font-bold text-slate-500 hover:bg-slate-50"
                aria-label="Mover campo"
              >
                ||
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
  function renderField(field) {
    if (!field || isControlField(field)) return null;
    const fieldReadOnly = isReadOnly || isFieldReadOnly(field);
    if (field.type === "title") {
      const textStyle = getTextStyle(field);
      const variantClass = getVariantClass(field.variant, "text-slate-700 font-semibold");
      const titleText =
        isReadOnly && values[field.name] !== undefined && values[field.name] !== null
          ? values[field.name]
          : field.text || field.value || "";
      return (
        <p key={field.name} className={`text-sm ${variantClass}`} style={textStyle}>
          {titleText}
        </p>
      );
    }
    if (field.type === "subtitle") {
      const textStyle = getTextStyle(field);
      const variantClass = getVariantClass(field.variant, "text-slate-500");
      const subtitleText =
        isReadOnly && values[field.name] !== undefined && values[field.name] !== null
          ? values[field.name]
          : field.text || field.value || "";
      return (
        <p key={field.name} className={`text-sm ${variantClass}`} style={textStyle}>
          {subtitleText}
        </p>
      );
    }
    if (field.type === "textView") {
      const variant = field.variant || "muted";
      const variantClass =
        variant === "strong"
          ? "text-slate-800 font-semibold"
          : variant === "subtle"
            ? "text-slate-400"
            : "text-slate-600";
      const textStyle = getTextStyle(field);
      const textValue =
        isReadOnly && values[field.name] !== undefined && values[field.name] !== null
          ? values[field.name]
          : field.text || field.value || "";
      return (
        <p key={field.name} className={`text-sm ${variantClass}`} style={textStyle}>
          {textValue}
        </p>
      );
    }
    if (field.type === "htmlview") {
      const variant = field.variant || "muted";
      const variantClass =
        variant === "strong"
          ? "text-slate-800 font-semibold"
          : variant === "subtle"
            ? "text-slate-400"
            : "text-slate-600";
      const htmlValue =
        isReadOnly && values[field.name] !== undefined && values[field.name] !== null
          ? values[field.name]
          : field.html || field.text || field.value || "";
      return (
        <p
          key={field.name}
          className={`text-sm ${variantClass}`}
          dangerouslySetInnerHTML={{ __html: String(htmlValue) }}
        />
      );
    }
    if (field.type === "adder") {
      const caption = field.caption || "Agregar";
      const groupValue = getGroupValue(field.adderGroup);
      const isAdderDisabled = fieldReadOnly || isReadOnly || !groupValue || isBuilderMode;
      return (
        <div key={field.name || field._id || `adder-${caption}`} className="flex items-center justify-center">
          <button
            type="button"
            disabled={isAdderDisabled}
            onClick={() => handleAdderClick(field)}
            className={`mt-1 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 ${
              isAdderDisabled ? "cursor-not-allowed opacity-60" : "hover:text-slate-700"
            }`}
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-slate-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M8 4a.5.5 0 0 1 .5.5V7h2.5a.5.5 0 0 1 0 1H8.5v2.5a.5.5 0 0 1-1 0V8H5a.5.5 0 0 1 0-1h2.5V4.5A.5.5 0 0 1 8 4Z" />
              </svg>
            </span>
            <span className="truncate">{caption}</span>
          </button>
        </div>
      );
    }
    if (field.type === "rating") {
      const ratingValue = Number(values[field.name]) || 0;
      const commentKey = getRatingCommentKey(field.name);
      const commentValue = String(coalesce(values[commentKey], ""));
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          <div className="mt-2 flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => {
              const active = ratingValue >= n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    if (fieldReadOnly) return;
                    setFieldValue(field.name, n);
                  }}
                  disabled={fieldReadOnly}
                  className={`text-2xl leading-none transition ${active ? "text-yellow-400" : "text-slate-300"} hover:text-yellow-400`}
                  aria-label={`${n} estrella${n === 1 ? "" : "s"}`}
                >
                  ★
                </button>
              );
            })}
          </div>
          {fieldReadOnly ? (
            commentValue ? <p className="mt-2 text-sm text-slate-600">{commentValue}</p> : null
          ) : (
            <textarea
              rows={2}
              value={commentValue}
              onChange={(event) => setFieldValue(commentKey, event.target.value)}
              placeholder="Comentario (opcional)"
              readOnly={fieldReadOnly}
              className={`mt-2 w-full resize-none rounded-xl ${surfaceBgClass} px-3 py-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
            />
          )}
        </div>
      );
    }
    if (field.type === "slider") {
      const options = Array.isArray(field.options) ? field.options : [];
      const currentIndex = Math.max(0, options.findIndex((opt) => opt === values[field.name]));
      const safeIndex = currentIndex >= 0 ? currentIndex : 0;
      const currentValue = coalesce(options[safeIndex], "");
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          <div className="mt-2">
            <input
              type="range"
              min="0"
              max={Math.max(0, options.length - 1)}
              step="1"
              value={safeIndex}
              onChange={(event) => {
                if (fieldReadOnly) return;
                const index = Number(event.target.value);
                const next = coalesce(coalesce(options[index], options[0]), "");
                setFieldValue(field.name, next);
              }}
              aria-disabled={fieldReadOnly}
              tabIndex={fieldReadOnly ? -1 : 0}
              readOnly={fieldReadOnly}
              className={`w-full accent-[var(--primary)] ${fieldReadOnly ? "pointer-events-none" : ""}`}
            />
            <div className="mt-0.5 flex items-center justify-between text-[11px] text-slate-500">
              <span>{coalesce(options[0], "")}</span>
              <span className="inline-flex items-center rounded-full bg-[var(--primary)] px-2 py-[2px] text-[10px] font-semibold leading-none text-white">
                {currentValue}
              </span>
              <span>{options.length ? options[options.length - 1] : ""}</span>
            </div>
          </div>
        </div>
      );
    }
    if (field.type === "pain") {
      const selectedIndex = getSelectedIndex(values[field.name], PAIN_KEYS);
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          <div className="mt-2 grid grid-cols-5 gap-2">
            {PAIN_SCALE.map((item, index) => {
              const isActive = selectedIndex === index;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => {
                    if (fieldReadOnly) return;
                    setFieldValue(field.name, index + 1);
                  }}
                  disabled={fieldReadOnly}
                  aria-pressed={isActive}
                  aria-label={item.label}
                  className={`flex h-12 w-full items-center justify-center rounded-xl text-2xl transition ${
                    isActive
                      ? "bg-[var(--primary-50)] ring-2 ring-[var(--primary)]"
                      : `${surfaceBgClass} ring-1 ring-slate-200 ${surfaceHoverClass}`
                  }`}
                >
                  <span aria-hidden="true">{item.emoji}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
            <span>Aliviado</span>
            <span>Mucho dolor</span>
          </div>
        </div>
      );
    }
    if (field.type === "selector") {
      const options = getSelectorOptions(field);
      const selectedIndex = getSelectedIndex(values[field.name], options);
      const columnCount = Math.max(1, options.length);
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          {options.length ? (
            <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}>
              {options.map((opt, index) => {
                const isActive = selectedIndex === index;
                const imgSrc = getSelectorImageSrc(opt);
                return (
                  <button
                    key={`${field.name}-selector-${index}`}
                    type="button"
                    onClick={() => {
                      if (fieldReadOnly) return;
                      setFieldValue(field.name, index + 1);
                    }}
                    disabled={fieldReadOnly}
                    aria-pressed={isActive}
                    aria-label={`Opción ${index + 1}`}
                  className={`flex h-12 w-full items-center justify-center overflow-hidden rounded-xl transition ${
                    isActive ? "bg-[var(--primary-50)] ring-2 ring-[var(--primary)]" : "bg-white ring-1 ring-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {imgSrc ? (
                      <img src={imgSrc} alt={`Opción ${index + 1}`} className="h-full w-full object-cover" />
                  ) : null}
                </button>
              );
            })}
            </div>
          ) : (
            <div className={`mt-2 rounded-xl ${surfaceBgClass} px-3 py-2 text-sm text-slate-400 ring-1 ring-slate-200`}>
              Sin opciones
            </div>
          )}
        </div>
      );
    }
    if (field.type === "bool") {
      return (
        <div key={field.name} className="block">
          <label className="block">
            <LabelText text={field.caption} required={field.required} />
            <div className={`mt-1 flex items-center justify-between gap-3 rounded-xl ${surfaceBgClass} px-3 py-2 text-sm ring-1 ring-slate-200`}>
              <span className="text-slate-600">Seleccione</span>
              <span className="relative inline-flex h-6 w-11 items-center">
                <input
                  type="checkbox"
                  checked={Boolean(values[field.name])}
                  onChange={(event) => setFieldValue(field.name, event.target.checked)}
                  required={field.required}
                  disabled={fieldReadOnly}
                  readOnly={fieldReadOnly}
                  className="peer sr-only"
                />
                <span
                  className={`h-6 w-11 rounded-full ${isDark ? "bg-slate-700" : "bg-slate-200"} transition peer-checked:bg-[var(--primary)]`}
                />
                <span
                  className="absolute left-1 h-4 w-4 rounded-full shadow transition peer-checked:translate-x-5"
                  style={isDark ? { backgroundColor: "#f8fafc" } : { backgroundColor: "#ffffff" }}
                />
              </span>
            </div>
          </label>
        </div>
      );
    }
    if (field.type === "image") {
      const imageSource = field.source || field.mode || "camera";
      const readOnlyValue = values[field.name];
      const previewSrc =
        (imagePreviews[field.name] && imagePreviews[field.name].url ? imagePreviews[field.name].url : "") ||
        getMediaObjectUrl(readOnlyValue) ||
        getMediaPreviewSrc(readOnlyValue) ||
        getMediaPreviewSrc(file_payloads[field.name]);
      const fileLabel = formatFileLabel(readOnlyValue);
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          <div className="mt-2 space-y-2">
            {fieldReadOnly ? (
              previewSrc ? (
                <div className="relative overflow-hidden rounded-xl ring-1 ring-slate-200">
                  <button type="button" onClick={() => openViewer("image", previewSrc, field.caption)} className="block w-full">
                    <img src={previewSrc} alt={field.caption} className="h-84 max-h-[460px] w-full object-cover" />
                  </button>
                </div>
              ) : (
                <div className={`rounded-xl ${surfaceBgClass} px-3 py-3 text-sm text-slate-600 ring-1 ring-slate-200`}>
                  {fileLabel}
                </div>
              )
            ) : imagePreviews[field.name] && imagePreviews[field.name].url ? (
              <>
                <div className="relative overflow-hidden rounded-xl ring-1 ring-slate-200">
                  <button
                    type="button"
                    onClick={() => openViewer("image", imagePreviews[field.name].url, field.caption)}
                    className="block w-full"
                  >
                    <img src={imagePreviews[field.name].url} alt={field.caption} className="h-84 max-h-[460px] w-full object-cover" />
                  </button>
                </div>
                <div className="mt-1 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setImageValue(field.name, null)}
                    className={`rounded-full ${surfaceBgClass} px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass}`}
                  >
                    Borrar
                  </button>
                </div>
              </>
            ) : (
              <>
                {imageSource === "upload" ? (
                  <div className="mt-1 flex items-center justify-start gap-2">
                    <label
                      className={`inline-flex cursor-pointer items-center justify-start rounded-full px-3 py-1 text-xs font-semibold ${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass} focus-within:ring-2 focus-within:ring-[var(--primary)]`}
                    >
                      Seleccionar imagen
                      <input
                        type="file"
                        accept="image/*"
                        required={field.required}
                        onChange={(event) => {
                          const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
                          setImageValue(field.name, file);
                        }}
                        readOnly={fieldReadOnly}
                        ref={(el) => {
                          if (el) imageInputRefs.current[field.name] = el;
                        }}
                        className="sr-only"
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    {cameraError.field === field.name && cameraError.message ? <p className="text-xs text-red-500">{cameraError.message}</p> : null}
                    {activeCameraField === field.name ? <p className="text-xs text-slate-500">Cámara activa</p> : null}
                    {activeCameraField === field.name ? (
                      <div className="overflow-hidden rounded-xl ring-1 ring-slate-200">
                        <video ref={videoRef} className="h-84 w-full object-cover" playsInline muted />
                      </div>
                    ) : null}
                    <div
                      className={`mt-1 flex flex-wrap items-center ${
                        activeCameraField === field.name ? "justify-center" : "justify-start"
                      } gap-x-2 gap-y-1`}
                    >
                      {activeCameraField === field.name ? (
                        <>
                          <button
                            type="button"
                            onClick={() => capturePhoto(field.name)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass} focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
                          >
                            Tomar foto
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleCamera(field.name, true)}
                            className={`rounded-full ${surfaceBgClass} px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass}`}
                          >
                            Cambiar
                          </button>
                          <button
                            type="button"
                            onClick={() => closeCamera(field.name)}
                            className={`rounded-full ${surfaceBgClass} px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass}`}
                          >
                            Cerrar
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openCamera(field.name)}
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass}`}
                        >
                          Abrir cámara
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      );
    }
    if (field.type === "video") {
      const isRecording = recordingField === field.name;
      const readOnlyValue = values[field.name];
      const readOnlyStream = getMediaStreamFromValue(readOnlyValue);
      const previewSrc =
        (videoPreviews[field.name] && videoPreviews[field.name].url ? videoPreviews[field.name].url : "") ||
        getMediaObjectUrl(readOnlyValue) ||
        getMediaPreviewSrc(readOnlyValue) ||
        getMediaPreviewSrc(file_payloads[field.name]);
      const fileLabel = formatFileLabel(readOnlyValue);
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          <div className="mt-2 space-y-2">
            {fieldReadOnly ? (
              previewSrc ? (
                <div className="relative overflow-hidden rounded-xl ring-1 ring-slate-200">
                  <video src={previewSrc} className="h-84 w-full object-contain bg-black" controls />
                </div>
              ) : readOnlyStream ? (
                <div className="relative overflow-hidden rounded-xl ring-1 ring-slate-200">
                  <StreamVideo stream={readOnlyStream} className="h-84 w-full object-contain bg-black" controls />
                </div>
              ) : (
                <div className={`rounded-xl ${surfaceBgClass} px-3 py-3 text-sm text-slate-600 ring-1 ring-slate-200`}>
                  {fileLabel}
                </div>
              )
            ) : videoPreviews[field.name] && videoPreviews[field.name].url ? (
              <>
                <div className="relative overflow-hidden rounded-xl ring-1 ring-slate-200">
                  <button
                    type="button"
                    onClick={() => openViewer("video", videoPreviews[field.name].url, field.caption)}
                    className="block w-full"
                  >
                    <video src={videoPreviews[field.name].url} className="h-84 w-full object-cover pointer-events-none" />
                  </button>
                </div>
                <div className="mt-1 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setVideoValue(field.name, null)}
                    className={`rounded-full ${surfaceBgClass} px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass}`}
                  >
                    Borrar
                  </button>
                </div>
              </>
            ) : (
              <>
                {cameraError.field === field.name && cameraError.message ? <p className="text-xs text-red-500">{cameraError.message}</p> : null}
                {activeCameraField === field.name ? (
                  <div className="overflow-hidden rounded-xl ring-1 ring-slate-200">
                    <video ref={videoRef} className="h-84 w-full object-cover" playsInline muted />
                  </div>
                ) : null}
                <div
                  className={`mt-1 flex flex-wrap items-center ${
                    activeCameraField === field.name ? "justify-center" : "justify-start"
                  } gap-x-2 gap-y-1`}
                >
                  {activeCameraField === field.name ? (
                    <>
                      <button
                        type="button"
                        onClick={() => startVideoRecording(field)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold focus:outline-none focus:ring-2 disabled:opacity-60 ${
                          isRecording
                            ? "bg-red-500 text-white hover:bg-red-600 focus:ring-red-500"
                            : `${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass} focus:ring-[var(--primary)]`
                        }`}
                      >
                        {isRecording ? `Terminar (${recordingSecondsLeft}s)` : `Grabar ${getRecordingSeconds(field)}s`}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleCamera(field.name)}
                        className={`rounded-full ${surfaceBgClass} px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass}`}
                      >
                        Cambiar
                      </button>
                      <button
                        type="button"
                        onClick={() => closeCamera(field.name)}
                        className={`rounded-full ${surfaceBgClass} px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass}`}
                      >
                        Cerrar
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openCamera(field.name, cameraFacing, true)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass}`}
                    >
                      Abrir cámara
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    if (field.type === "videoview") {
      const videoValue =
        fieldReadOnly && values[field.name] !== undefined && values[field.name] !== null
          ? values[field.name]
          : field.url || field.value || "";
      const videoUrl = typeof videoValue === "string" ? videoValue : "";
      const youtubeRaw = typeof field.youtube === "string" ? field.youtube : "";
      const youtubeEmbed = buildYouTubeEmbedUrl(youtubeRaw) || buildYouTubeEmbedUrl(videoUrl);
      const useYouTube = Boolean(youtubeEmbed);
      const videoHeight = normalizeHeight(field.height);
      const videoStyle = videoHeight ? { height: videoHeight } : undefined;
      const iframeStyle = videoHeight ? { height: videoHeight } : undefined;
      return (
        <div key={field.name} className="block">
          {field.caption ? <LabelText text={field.caption} required={field.required} /> : null}
          {useYouTube ? (
            <div className="mt-2 overflow-hidden rounded-xl ring-1 ring-slate-200" style={iframeStyle}>
              <iframe
                src={youtubeEmbed}
                title={field.caption || "Vídeo"}
                className="w-full"
                style={iframeStyle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          ) : videoUrl ? (
            <div className="mt-2 overflow-hidden rounded-xl ring-1 ring-slate-200" style={videoStyle}>
              <video
                src={videoUrl}
                controls
                className={`w-full ${videoHeight ? "h-full object-contain bg-black" : ""}`}
              />
            </div>
          ) : (
            <div className={`mt-2 rounded-xl ${surfaceBgClass} px-3 py-2 text-sm text-slate-400 ring-1 ring-slate-200`}>
              Sin video
            </div>
          )}
        </div>
      );
    }
    if (field.type === "imageview") {
      const fieldImages = getImageViewOptions(field);
      const valueImages = Array.isArray(values[field.name]) ? values[field.name].filter(Boolean) : [];
      const images = valueImages.length ? valueImages : fieldImages;
      const isSingle = images.length === 1;
      const alignmentRaw = typeof field.alignment === "string" ? field.alignment.toLowerCase() : "";
      const zoomEnabled = field.zoom === true || String(field.zoom).toLowerCase() === "true";
      const alignClass =
        alignmentRaw === "center" ? "justify-center" : alignmentRaw === "right" ? "justify-end" : alignmentRaw === "left" ? "justify-start" : "";
      const alignGridClass =
        alignmentRaw === "center"
          ? "justify-items-center"
          : alignmentRaw === "right"
            ? "justify-items-end"
            : alignmentRaw === "left"
              ? "justify-items-start"
              : "";
      const imageHeight = normalizeHeight(field.height);
      const imageBoxStyle = imageHeight ? { height: imageHeight } : undefined;
      const imageClassName = `w-full object-cover ${imageHeight ? "h-full" : isSingle ? "h-84" : "h-62"}`;
      const imageStyle = { objectPosition: "center top" };
      return (
        <div key={field.name} className="block">
          {field.caption ? <LabelText text={field.caption} required={field.required} /> : null}
          <div className={`mt-2 ${isSingle ? `flex ${alignClass}` : `grid grid-cols-2 gap-2 ${alignGridClass}`}`}>
            {images.map((src, index) => (
              <div
                key={`${field.name}-${index}`}
                className="overflow-hidden rounded-xl ring-1 ring-slate-200"
                style={imageBoxStyle}
              >
                {zoomEnabled ? (
                  <button
                    type="button"
                    onClick={() => openViewer("image", src, field.caption ? `${field.caption} ${index + 1}` : `Imagen ${index + 1}`)}
                    className={`block w-full ${imageHeight ? "h-full" : ""}`}
                  >
                    <img
                      src={src}
                      alt={field.caption ? `${field.caption} ${index + 1}` : `Imagen ${index + 1}`}
                      className={imageClassName}
                      style={imageStyle}
                    />
                  </button>
                ) : (
                  <div className={`block w-full ${imageHeight ? "h-full" : ""}`}>
                    <img
                      src={src}
                      alt={field.caption ? `${field.caption} ${index + 1}` : `Imagen ${index + 1}`}
                      className={imageClassName}
                      style={imageStyle}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (field.type === "divider") {
      return <div key={field.name} className="border-t border-slate-200" />;
    }
    if (field.type === "singlelist") {
      const options = Array.isArray(field.options) ? field.options : [];
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          <div className="mt-2 space-y-2 text-sm">
            {options.map((opt) => {
              const isSelected = values[field.name] === opt;
              return (
                <label
                  key={opt}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                    fieldReadOnly && isSelected ? "border-[var(--primary-200)] bg-[var(--primary-50)]" : "border-slate-200"
                  } ${fieldReadOnly ? "pointer-events-none" : ""}`}
                >
                  <input
                    type="radio"
                    name={`singlelist-${field.name}`}
                    checked={isSelected}
                    onChange={() => setFieldValue(field.name, opt)}
                    aria-disabled={fieldReadOnly}
                    tabIndex={fieldReadOnly ? -1 : 0}
                    readOnly={fieldReadOnly}
                    className={`accent-[var(--primary)] ${fieldReadOnly && isSelected ? "opacity-100" : ""}`}
                  />
                  <span className={fieldReadOnly && isSelected ? "text-slate-700 font-medium" : ""}>{opt}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }
    if (field.type === "multiplelist") {
      const options = Array.isArray(field.options) ? field.options : [];
      const selectedValues = Array.isArray(values[field.name]) ? values[field.name] : [];
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          <div className="mt-2 space-y-2 text-sm">
            {options.map((opt) => {
              const isSelected = selectedValues.includes(opt);
              return (
                <label
                  key={opt}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                    fieldReadOnly && isSelected ? "border-[var(--primary-200)] bg-[var(--primary-50)]" : "border-slate-200"
                  } ${fieldReadOnly ? "pointer-events-none" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleMultipleValue(field.name, opt)}
                    aria-disabled={fieldReadOnly}
                    tabIndex={fieldReadOnly ? -1 : 0}
                    readOnly={fieldReadOnly}
                    className={`accent-[var(--primary)] ${fieldReadOnly && isSelected ? "opacity-100" : ""}`}
                  />
                  <span className={fieldReadOnly && isSelected ? "text-slate-700 font-medium" : ""}>{opt}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }
    if (field.type === "attach") {
      const attachUrlRaw = (attachPreviews[field.name] && attachPreviews[field.name].url) || getMediaPreviewSrc(values[field.name]);
      const attachUrl = typeof attachUrlRaw === "string" ? attachUrlRaw.trim() : "";
      const fileLabel = formatFileLabel(values[field.name]);
      const hasAttachValue = Boolean(values[field.name]);
      return (
        <div key={field.name} className="block">
          <div className="flex items-center justify-between gap-2">
            <LabelText text={field.caption} required={field.required} />
          </div>
          {fieldReadOnly ? (
            <>
              <div className={`mt-2 rounded-xl ${surfaceBgClass} px-3 py-3 text-sm text-slate-600 ring-1 ring-slate-200`}>{fileLabel}</div>
              {attachUrl ? (
                <div className="mt-1 flex items-center justify-start gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!attachUrl) return;
                      openViewer("file", attachUrl, field.caption);
                    }}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${softButtonClass}`}
                  >
                    Ver
                  </button>
                  <a
                    href={attachUrl}
                    download
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${softButtonClass}`}
                  >
                    Descargar
                  </a>
                </div>
              ) : null}
            </>
          ) : (
            <div className="mt-2 space-y-2">
              {hasAttachValue ? (
                <div>
                  <div className={`flex items-center justify-between gap-2 rounded-xl ${surfaceBgClass} px-3 py-3 text-sm text-slate-600 ring-1 ring-slate-200`}>
                    <span>{fileLabel}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setAttachValue(field.name, null);
                        clearAttachInput(field.name);
                      }}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${softButtonClass}`}
                    >
                      Borrar
                    </button>
                  </div>
                  {attachUrl ? (
                    <div className="mt-1 flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!attachUrl) return;
                          openViewer("file", attachUrl, field.caption);
                        }}
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${softButtonClass}`}
                      >
                        Ver
                      </button>
                      <a
                        href={attachUrl}
                        download
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${softButtonClass}`}
                      >
                        Descargar
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="mt-1 flex items-center justify-start gap-2">
                  <label
                    className={`inline-flex cursor-pointer items-center justify-start rounded-full px-3 py-1 text-xs font-semibold ${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass} focus-within:ring-2 focus-within:ring-[var(--primary)]`}
                  >
                    Seleccionar archivo
                    <input
                      type="file"
                      accept={normalizeExtensions(field.extensions)}
                      onChange={(event) => {
                        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
                        setAttachValue(field.name, file);
                      }}
                      required={field.required}
                      readOnly={fieldReadOnly}
                      ref={(el) => {
                        if (el) attachInputRefs.current[field.name] = el;
                      }}
                      className="sr-only"
                    />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    if (field.type === "code") {
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          <div className="mt-1 flex items-center justify-start gap-2">
            <button
              type="button"
              disabled={fieldReadOnly}
              onClick={() => {
                if (fieldReadOnly) return;
                setResult("code");
              }}
              className={`inline-flex items-center justify-start rounded-full px-3 py-1 text-xs font-semibold ${
                fieldReadOnly
                  ? `${surfaceBgClass} text-slate-400 ring-1 ring-slate-200`
                  : `${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass} focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`
              }`}
            >
              Leer código
            </button>
          </div>
        </div>
      );
    }
    if (field.type === "memo") {
      const isDictating = dictationField === field.name;
      const hasDictation = field.dictation || field.dictado;
      const memoValueRaw = coalesce(values[field.name], "");
      const memoValue = memoValueRaw === 0 ? "" : memoValueRaw;
      const currentLength = String(memoValue).length;
      const isAtLimit = currentLength >= MAX_DICTATION_CHARS;
      const showListening = isDictating && dictationPhase === "listening";
      const showProcessing = isDictating && dictationPhase === "processing";
      const showPreview = isDictating && dictationPreview.field === field.name && Boolean(dictationPreview.text);
      const hasDictationMessage =
        (dictationError.field === field.name && dictationError.message) ||
        showListening ||
        showProcessing ||
        !supportsDictation;
        
      return (
        <label key={field.name} className="block">
          <div className="flex items-center justify-between gap-2">
            <LabelText text={field.caption} required={field.required} />
          </div>
          <textarea
            rows={3}
            value={memoValue}
            placeholder={field.placeholder}
            onChange={(event) => {
              setFieldValue(field.name, event.target.value);
              autoResizeTextarea(event.target);
            }}
            onInput={(event) => autoResizeTextarea(event.target)}
            required={field.required}
            disabled={fieldReadOnly}
            readOnly={fieldReadOnly}
            ref={(el) => autoResizeTextarea(el)}
            className={`mt-1 w-full resize-none overflow-hidden rounded-xl ${surfaceBgClass} px-3 py-3 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
          />
          {hasDictation && !fieldReadOnly ? (
            <div className="mt-0 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => startDictation(field.name)}
                disabled={!supportsDictation || isAtLimit}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  isDictating
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : `${surfaceBgStrongClass} text-slate-700 ring-1 ring-slate-200 ${surfaceHoverStrongClass}`
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {isDictating ? "Detener dictado" : "Dictar"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isDictating) {
                    stopDictation();
                  }
                  setFieldValue(field.name, "");
                }}
                className={`rounded-full ${surfaceBgClass} px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass}`}
              >
                Borrar
              </button>
            </div>
          ) : null}
          {showPreview ? (
            <div className="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">
              {dictationPreview.text}
            </div>
          ) : null}
          {hasDictation && !fieldReadOnly && hasDictationMessage ? (
            <div className="mt-2 flex items-center gap-2">
              {dictationError.field === field.name && dictationError.message ? (
                <span className="text-xs text-red-500">{dictationError.message}</span>
              ) : null}
              {showListening ? <span className="text-xs text-slate-500">Escuchando...</span> : null}
              {showProcessing ? <span className="text-xs text-slate-500 animate-pulse">Procesando...</span> : null}
              {!supportsDictation ? <span className="text-xs text-slate-500">Dictado no disponible</span> : null}
            </div>
          ) : null}
        </label>
      );
    }
    if (field.type === "options") {
      const options = Array.isArray(field.options) ? field.options : [];
      const currentValue = coalesce(values[field.name], "");
      const isOpen = openSelectField === field.name;
      return (
        <div key={field.name} className="block">
          <LabelText text={field.caption} required={field.required} />
          {fieldReadOnly ? (
            <div className={`mt-1 rounded-xl ${surfaceBgClass} px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200`}>
              {currentValue || "Sin selección"}
            </div>
          ) : (
            <div className="relative mt-1" data-select-root>
              <button
                type="button"
                onClick={() => setOpenSelectField(isOpen ? null : field.name)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                className={`flex w-full items-center justify-between rounded-xl ${surfaceBgClass} px-3 py-2 text-sm text-slate-700 ring-1 ring-slate-200 ${surfaceHoverClass} focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
              >
                <span className={currentValue ? "" : "text-slate-400"}>{currentValue || "Seleccione..."}</span>
                <span className={`text-slate-400 transition ${isOpen ? "rotate-180" : ""}`}>▾</span>
              </button>
              {isOpen ? (
                <div className="absolute z-20 mt-2 w-full rounded-xl bg-white shadow-lg ring-1 ring-slate-200">
                  <div className="max-h-60 overflow-auto py-1 text-sm">
                    {options.map((opt) => {
                      const isSelected = currentValue === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => {
                            setFieldValue(field.name, opt);
                            setOpenSelectField(null);
                          }}
                          className={`w-full px-3 py-2 text-left ${
                            isSelected ? "bg-[var(--primary-50)] text-slate-700 font-medium" : "text-slate-700"
                          } hover:bg-slate-50`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      );
    }
    return (
      <label key={field.name} className="block">
        <LabelText text={field.caption} required={field.required} />
        {field.type === "date" ? (
          <>
            <input
              type="date"
              value={coalesce(values[field.name], "")}
              placeholder={field.placeholder}
              onChange={(event) => setFieldValue(field.name, event.target.value)}
              required={field.required}
              disabled={fieldReadOnly}
              readOnly={fieldReadOnly}
              aria-invalid={isOutOfRange(field, values[field.name])}
              className={`mt-1 w-full rounded-xl ${surfaceBgClass} px-3 py-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
            />
            {isOutOfRange(field, values[field.name]) ? <p className="mt-1 text-xs text-red-500">Fuera de rango</p> : null}
          </>
        ) : field.type === "time" ? (
          <>
            <input
              type="time"
              value={coalesce(values[field.name], "")}
              placeholder={field.placeholder}
              onChange={(event) => setFieldValue(field.name, event.target.value)}
              required={field.required}
              disabled={fieldReadOnly}
              readOnly={fieldReadOnly}
              aria-invalid={isOutOfRange(field, values[field.name])}
              className={`mt-1 w-full rounded-xl ${surfaceBgClass} px-3 py-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
            />
            {isOutOfRange(field, values[field.name]) ? <p className="mt-1 text-xs text-red-500">Fuera de rango</p> : null}
          </>
        ) : field.type === "datetime" ? (
          <>
            <input
              type="datetime-local"
              value={coalesce(values[field.name], "")}
              placeholder={field.placeholder}
              onChange={(event) => setFieldValue(field.name, event.target.value)}
              required={field.required}
              disabled={fieldReadOnly}
              readOnly={fieldReadOnly}
              aria-invalid={isOutOfRange(field, values[field.name])}
              className={`mt-1 w-full rounded-xl ${surfaceBgClass} px-3 py-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
            />
            {isOutOfRange(field, values[field.name]) ? <p className="mt-1 text-xs text-red-500">Fuera de rango</p> : null}
          </>
        ) : (
          <>
            <input
              type={field.type === "number" ? "number" : "text"}
              value={coalesce(values[field.name], "")}
              placeholder={field.placeholder}
              onChange={(event) => {
                const nextValue =
                  field.type === "number"
                    ? sanitizeNumberInput(field, event.target.value)
                    : event.target.value;
                setFieldValue(field.name, nextValue);
              }}
              required={field.required}
              disabled={fieldReadOnly}
              readOnly={fieldReadOnly}
              maxLength={
                field.type === "number"
                  ? resolveNumberMaxLength(field)
                  : field.type === "text" && typeof field.maxLength === "number"
                    ? field.maxLength
                    : undefined
              }
              step={field.type === "number" ? resolveNumberStep(field) : undefined}
              inputMode={field.type === "number" && isTrueValue(field.integer) ? "numeric" : undefined}
              pattern={field.type === "number" && isTrueValue(field.integer) ? "[0-9]*" : undefined}
              aria-invalid={field.type === "number" ? isOutOfRange(field, values[field.name]) : undefined}
              className={`mt-1 w-full rounded-xl ${surfaceBgClass} px-3 py-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]`}
            />
            {field.type === "number" && isOutOfRange(field, values[field.name]) ? <p className="mt-1 text-xs text-red-500">Fuera de rango</p> : null}
          </>
        )}
      </label>
    );
  }
  function renderFieldsWithLayoutForList(fieldsList) {
    const rendered = [];
    let layoutMode = "layout-1";
    let layoutFreeOptions = [];
    let i = 0;
    while (i < fieldsList.length) {
      const field = fieldsList[i];
      if (!field) {
        i += 1;
        continue;
      }
      if (isLayoutField(field)) {
        layoutMode = field.type;
        if (field.type === "layout-free") {
          layoutFreeOptions = normalizeLayoutFreeOptions(field.options);
        }
        i += 1;
        continue;
      }
      if (layoutMode === "layout-free") {
        if (!layoutFreeOptions || layoutFreeOptions.length === 0) {
          layoutMode = "layout-1";
          continue;
        }
        const collected = [];
        let nextLayoutMode = layoutMode;
        let nextLayoutFreeOptions = layoutFreeOptions;
        let j = i;
        while (j < fieldsList.length && collected.length < layoutFreeOptions.length) {
          const candidate = fieldsList[j];
          if (!candidate) {
            j += 1;
            continue;
          }
          if (isLayoutField(candidate)) {
            nextLayoutMode = candidate.type;
            if (candidate.type === "layout-free") {
              nextLayoutFreeOptions = normalizeLayoutFreeOptions(candidate.options);
            }
            j += 1;
            if (candidate.type === "layout-1") {
              break;
            }
            continue;
          }
          collected.push(candidate);
          j += 1;
        }
        const lastFieldIdx = collected.length - 1;
        rendered.push(
          <div
            key={`layout-free-${i}`}
            className="grid gap-3 items-start [&>*]:min-w-0 [&_[data-label-text]]:truncate"
            style={{ gridTemplateColumns: layoutFreeOptions.join(" ") }}
          >
            {layoutFreeOptions.map((width, idx) => {
              const targetField = collected[idx];
              if (!targetField) {
                return <div key={`layout-free-empty-${i}-${idx}-${width}`} />;
              }
              return (
                <div key={`layout-free-col-${i}-${idx}-${width}`} className="min-w-0">
                  {renderFieldWithBuilder(targetField, { hideHandle: idx < lastFieldIdx })}
                </div>
              );
            })}
          </div>
        );
        layoutMode = nextLayoutMode;
        layoutFreeOptions = nextLayoutFreeOptions;
        i = j;
        continue;
      }
      if (layoutMode === "layout-2") {
        const leftField = field;
        let rightField = null;
        let nextLayoutMode = layoutMode;
        let j = i + 1;
        while (j < fieldsList.length) {
          const candidate = fieldsList[j];
          if (!candidate) {
            j += 1;
            continue;
          }
          if (isLayoutField(candidate)) {
            nextLayoutMode = candidate.type;
            j += 1;
            if (candidate.type === "layout-1") {
              break;
            }
            continue;
          }
          rightField = candidate;
          j += 1;
          break;
        }
        if (rightField) {
          rendered.push(
            <div key={`layout-2-${i}`} className="grid grid-cols-2 gap-3 items-start [&>*]:min-w-0 [&_[data-label-text]]:truncate">
              {renderFieldWithBuilder(leftField, { hideHandle: true })}
              {renderFieldWithBuilder(rightField)}
            </div>
          );
        } else {
          rendered.push(renderFieldWithBuilder(leftField));
        }
        layoutMode = nextLayoutMode;
        i = j;
        continue;
      }
      if (layoutMode === "layout-3") {
        const firstField = field;
        let secondField = null;
        let thirdField = null;
        let nextLayoutMode = layoutMode;
        let j = i + 1;
        while (j < fieldsList.length) {
          const candidate = fieldsList[j];
          if (!candidate) {
            j += 1;
            continue;
          }
          if (isLayoutField(candidate)) {
            nextLayoutMode = candidate.type;
            j += 1;
            if (candidate.type === "layout-1") {
              break;
            }
            continue;
          }
          secondField = candidate;
          j += 1;
          break;
        }
        if (!secondField) {
          rendered.push(renderFieldWithBuilder(firstField));
          layoutMode = nextLayoutMode;
          i = j;
          continue;
        }
        while (j < fieldsList.length) {
          const candidate = fieldsList[j];
          if (!candidate) {
            j += 1;
            continue;
          }
          if (isLayoutField(candidate)) {
            nextLayoutMode = candidate.type;
            j += 1;
            if (candidate.type === "layout-1") {
              break;
            }
            continue;
          }
          thirdField = candidate;
          j += 1;
          break;
        }
        rendered.push(
          <div key={`layout-3-${i}`} className="grid grid-cols-3 gap-3 items-start [&>*]:min-w-0 [&_[data-label-text]]:truncate">
            {renderFieldWithBuilder(firstField, { hideHandle: true })}
            {renderFieldWithBuilder(secondField, { hideHandle: true })}
            {thirdField ? renderFieldWithBuilder(thirdField) : null}
          </div>
        );
        layoutMode = nextLayoutMode;
        i = j;
        continue;
      }
      rendered.push(renderFieldWithBuilder(field));
      i += 1;
    }
    return rendered;
  }
  function getActiveTabLabel() {
    let activeLabel = activeTab;
    if (tabConfig.order.indexOf(activeLabel) === -1) {
      activeLabel = tabConfig.order[0];
    }
    return activeLabel;
  }
  function getActiveTabData(activeLabel) {
    for (let i = 0; i < tabConfig.tabs.length; i += 1) {
      const tab = tabConfig.tabs[i];
      if (tab.label === activeLabel) {
        return tab;
      }
    }
    return null;
  }
  function renderTabButtons(activeLabel) {
    return (
      <div
        className={`rounded-xl p-2 ${tabBarBackground ? "" : "bg-slate-50"}`}
        style={tabBarBackground ? { backgroundColor: tabBarBackground } : undefined}
      >
        <div className="flex flex-wrap gap-2" role="tablist">
          {tabConfig.tabs.map((tab) => {
            const isActive = tab.label === activeLabel;
            return (
              <button
                key={`tab-${tab.label}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.label)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${
                  isActive
                    ? "bg-[var(--primary-100)] text-slate-700 ring-[var(--primary-200)]"
                    : `${surfaceBgClass} text-slate-600 ring-slate-200 ${surfaceHoverClass}`
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  function renderFieldsWithLayout() {
    if (!tabConfig.hasTabs) {
      return renderFieldsWithLayoutForList(fields);
    }
    const rendered = [];
    const beforeRendered = renderFieldsWithLayoutForList(tabConfig.before);
    for (let i = 0; i < beforeRendered.length; i += 1) {
      rendered.push(beforeRendered[i]);
    }
    const activeLabel = getActiveTabLabel();
    const activeTabData = getActiveTabData(activeLabel);
    rendered.push(
      <div key="tab-group" className="space-y-2.5">
        {renderTabButtons(activeLabel)}
        <div className="space-y-2.5">
          {activeTabData ? renderFieldsWithLayoutForList(activeTabData.fields) : null}
        </div>
      </div>
    );
    return rendered;
  }
  const tabsFirst = tabConfig.hasTabs && tabConfig.before.length === 0;
  const activeTabLabel = tabConfig.hasTabs ? getActiveTabLabel() : "";
  const activeTabData = tabConfig.hasTabs ? getActiveTabData(activeTabLabel) : null;
  const builderControlsWidth = 56;
  const cardSectionStyle = isBuilderMode ? { ...sectionStyle, "--builder-controls-width": builderControlsWidth + "px" } : sectionStyle;
  return (
    <section
      ref={rootRef}
      className={cardClassName}
      style={cardSectionStyle}
      onClick={(event) => {
        if (!canSelectForm) return;
        if (event.target.closest && event.target.closest("[data-builder-field]")) return;
        onBuilderSelectForm();
      }}
    >
      {isDark ? <style>{darkModeStyles}</style> : null}
      {viewer.open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeViewer();
            }
          }}
        >
          <button
            type="button"
            onClick={closeViewer}
            className="fixed right-5 top-5 z-[60] inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow hover:bg-white"
            style={{ color: "#0f172a" }}
            aria-label="Cerrar vista completa"
          >
            ✕
          </button>
          <div className="relative w-full max-w-5xl">
            <div className="overflow-hidden rounded-2xl bg-black">
              {viewer.type === "video" ? (
                <video src={viewer.src} className="max-h-[85vh] w-full object-contain" controls autoPlay />
              ) : viewer.type === "file" ? (
                <div className="bg-white">
                  <iframe
                    src={getAttachViewerSrc(viewer.src)}
                    title={viewer.caption || "Adjunto"}
                    className="h-[85vh] w-full"
                  />
                </div>
              ) : (
                <>
                  {!viewerImageError ? (
                    <img
                      src={viewer.src}
                      alt={viewer.caption || "Vista completa"}
                      className="max-h-[85vh] w-full object-contain"
                      onError={() => setViewerImageError(true)}
                    />
                  ) : (
                    <iframe
                      src={getAttachViewerSrc(viewer.src)}
                      title={viewer.caption || "Vista completa"}
                      className="h-[85vh] w-full"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {showHeader && showClose ? (
        <button
          type="button"
          onClick={() => setResult("cerrar")}
          className="absolute right-3 top-3 inline-flex h-6 w-6 items-center justify-center rounded-full text-red-500 hover:bg-red-50"
          aria-label="Cerrar tarjeta"
        >
          ✕
        </button>
      ) : null}
      {showHeader ? (
        hasHtmlHeader ? (
          <div dangerouslySetInnerHTML={{ __html: html_header }} />
        ) : (
          <header>
            <div className="flex items-center gap-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 ring-1 ring-slate-200">
                {headerIcon}
              </div>
              <div className="flex-1">
                <div className="flex min-h-[3rem] flex-col justify-center">
                  <h2 className="text-lg font-semibold">{title}</h2>
                  {hasSubtitle ? <p className="text-sm text-slate-500">{subtitle}</p> : scheduleLine}
                </div>
              </div>
            </div>
            {hasSubtitle ? <div className="mt-1">{scheduleLine}</div> : null}
          </header>
        )
      ) : null}
      {tabsFirst && scrollHeight ? (
        <div style={fixedHeightStyle} className={`${showHeader ? "mt-4" : ""} flex min-h-0 flex-col space-y-2.5`}>
          {renderTabButtons(activeTabLabel)}
          <div style={innerScrollStyle} className="flex-1 min-h-0 space-y-2.5">
            {activeTabData ? renderFieldsWithLayoutForList(activeTabData.fields) : null}
            <canvas ref={canvasRef} className="hidden" />
            {showProgramacion ? (
              <Programacion
                value={effectiveProgramacionValue}
                onChange={handleProgramacionChange}
                dayKeys={DAY_KEYS}
                surfaceBgClass={surfaceBgClass}
                surfaceHoverClass={surfaceHoverClass}
                isReadOnly={isReadOnly}
              />
            ) : null}
            {showNotificacionWithFieldSelect ? renderNotificacionSiConCampo(inputFieldOptions) : null}
            {submitMode ? (
              <div className="hidden">
                {Object.keys(resultPayload).map((key) => (
                  <input key={key} type="hidden" name={key} value={serializeSubmitValue(resultPayload[key])} readOnly={isReadOnly} />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div style={scrollContainerStyle} className={showHeader ? "mt-4 space-y-2.5" : "space-y-2.5"}>
          {renderFieldsWithLayout()}
          <canvas ref={canvasRef} className="hidden" />
          {showProgramacion ? (
            <Programacion
              value={effectiveProgramacionValue}
              onChange={handleProgramacionChange}
              dayKeys={DAY_KEYS}
              surfaceBgClass={surfaceBgClass}
              surfaceHoverClass={surfaceHoverClass}
              isReadOnly={isReadOnly}
            />
          ) : null}
          {showNotificacionWithFieldSelect ? renderNotificacionSiConCampo(inputFieldOptions) : null}
          {submitMode ? (
            <div className="hidden">
                {Object.keys(resultPayload).map((key) => (
                  <input key={key} type="hidden" name={key} value={serializeSubmitValue(resultPayload[key])} readOnly={isReadOnly} />
                ))}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
